import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  useListLeagueCallups,
  getListLeagueCallupsQueryKey,
  CallupSummary,
} from "@workspace/api-client-react";
import { resolveAssetUrl } from "@/lib/assets";
import { formatCallupDateLabel, formatCallupTimeLabel } from "@/lib/callupFormatting";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  X,
  Trophy,
  Users,
  TrendingUp,
  TrendingDown,
  Calendar,
  Copy,
  Check,
  Medal,
  Swords,
  ListOrdered,
  ChartColumn,
  Clock,
  ArrowUp,
  ArrowDown,
  Minus,
  Share2,
  Settings,
  ImageIcon,
  Trash2,
  Upload,
  Loader2,
  Pencil,
  MoreVertical,
  Shield,
  ShieldCheck,
  Crown,
  UserMinus,
  UserPlus,
  CheckCircle,
  XCircle,
  AlertCircle,
  LogOut,
  HelpCircle,
  Info,
  Plus,
  Megaphone,
  MapPin,
  Lock,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { nativeCopy, hasNativeCopy, nativeShare, hasNativeShare } from "@/lib/nativeBridge";
import { leagueCallupUrl, leagueInviteUrl } from "@/lib/publicLinks";

interface LeagueDetailViewProps {
  leagueId: number | string;
  onClose: () => void;
  initialTab?: string;
  /** Pre-focuses a sub-tab inside the Eventos tab (e.g. "torneos" when
   * arriving from a tournament notification). */
  initialEventosSubTab?: "convocatorias" | "americanos" | "torneos";
  onTabChange?: (tab: string) => void;
}

interface LeagueMember {
  id: string;
  leagueId: string;
  userId: string;
  userName: string;
  userImage?: string;
  role: "owner" | "admin" | "member";
  rating: number;
  matchesPlayed: number;
  matchesWon: number;
  matchesLost: number;
  rank: number;
}

import { PlayerPreviewModal } from "./PlayerPreviewModal";
import { MatchHistoryDrawer } from "./MatchHistoryDrawer";
import { EditPendingMatchDialog } from "./EditPendingMatchDialog";
import { EditScheduledMatchDialog } from "./EditScheduledMatchDialog";
import { RegisterScheduledMatchResultDialog } from "./RegisterScheduledMatchResultDialog";
import { LeagueTournamentsSection } from "./LeagueTournamentsSection";
import { LeagueAmericanosSection } from "./LeagueAmericanosSection";
import {
  LockedScheduledResultDialog,
  isScheduledMatchLocked,
} from "./LockedScheduledResultDialog";
import { CompletedScheduledMatchDetailsDialog } from "./CompletedScheduledMatchDetailsDialog";

interface RatingChange {
  before: number;
  after: number;
  delta: number;
}

interface LeagueMatch {
  id: string;
  leagueId: string;
  homeClubId?: string | null;
  homeClubName?: string | null;
  team1Player1Id: string;
  team1Player1Name?: string;
  team1Player2Id?: string;
  team1Player2Name?: string;
  team2Player1Id: string;
  team2Player1Name?: string;
  team2Player2Id?: string;
  team2Player2Name?: string;
  winner: 1 | 2;
  status?: "pending" | "approved" | "rejected";
  reporterTeam?: 1 | 2;
  reportedById?: string;
  reportedByName?: string | null;
  recordedById?: string | null;
  recordedByName?: string | null;
  score?: string | { sets: Array<{ team1: number; team2: number }> };
  ratingChanges?: {
    team1Player1: RatingChange;
    team1Player2?: RatingChange;
    team2Player1: RatingChange;
    team2Player2?: RatingChange;
  };
  playedAt: string;
  createdAt: string;
  notes?: string;
}

/** Tennis/padel ball icon (lucide-style): circle with two curved seams. */
function TennisBallIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M4.9 5.1a10 10 0 0 1 0 13.8" />
      <path d="M19.1 5.1a10 10 0 0 0 0 13.8" />
    </svg>
  );
}

/** Calendar with star icon (lucide-style), for upcoming events. */
function CalendarStarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18" />
      <path d="m12 13 .9 1.85 2.1.3-1.5 1.45.35 2.05L12 17.7l-1.85.95.35-2.05-1.5-1.45 2.1-.3L12 13z" />
    </svg>
  );
}

const AUTO_APPROVAL_HOURS = 3;
function formatDeadlineTime(createdAt: string): string {
  const deadline = new Date(new Date(createdAt).getTime() + AUTO_APPROVAL_HOURS * 60 * 60 * 1000);
  return deadline.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit", hour12: true });
}

interface ScheduledMatchPlayerLite {
  id: string;
  name: string;
  image: string | null;
}

interface LeagueScheduledMatch {
  id: string;
  leagueId: string | null;
  organizerId: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  court?: string | null;
  homeClubId?: string | null;
  homeClubName?: string | null;
  notes?: string | null;
  status: "scheduled" | "score_pending" | "score_submitted" | "completed" | "cancelled";
  score?: { sets: Array<{ team1: number; team2: number }> } | null;
  winner?: 1 | 2 | null;
  approvedAt?: string | null;
  team1Player1Id: string;
  team1Player2Id?: string | null;
  team2Player1Id: string;
  team2Player2Id?: string | null;
  team1Player1: ScheduledMatchPlayerLite | null;
  team1Player2: ScheduledMatchPlayerLite | null;
  team2Player1: ScheduledMatchPlayerLite | null;
  team2Player2: ScheduledMatchPlayerLite | null;
}

interface LeagueData {
  league: {
    id: string;
    name: string;
    description?: string;
    imageUrl?: string;
    isPrivate: boolean;
    matchFormat: "singles" | "doubles";
    inviteCode: string;
    createdAt: string;
    homeClubId?: string | null;
    homeClubName?: string | null;
    ratingInitMode?: "skill_based" | "equal";
  };
  members: LeagueMember[];
  matches: LeagueMatch[];
  scheduledMatches?: LeagueScheduledMatch[];
  upcomingScheduled?: LeagueScheduledMatch[];
  pendingResultScheduled?: LeagueScheduledMatch[];
  pendingApprovalScheduled?: any[];
  pendingApprovalDirect?: any[];
  recentCompletedScheduled?: LeagueScheduledMatch[];
  userMembership: LeagueMember;
}

interface TrendingPlayer {
  userId: string;
  userName: string;
  userImage?: string;
  ratingChange: number;
  currentRating: number;
  matchesInPeriod: number;
}

interface JoinRequest {
  id: string;
  leagueId: string;
  userId: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  processedAt?: string;
  processedById?: string;
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    profileImageUrl?: string;
    email?: string;
  };
}

interface LeagueSeason {
  id: string;
  leagueId: string;
  seasonNumber: number;
  name: string;
  status: "active" | "ended";
  startedAt: string;
  endedAt?: string;
  createdAt: string;
}

interface SeasonMember {
  id: string;
  seasonId: string;
  leagueId: string;
  userId: string;
  finalRating: number;
  finalRank: number;
  matchesPlayed: number;
  matchesWon: number;
  matchesLost: number;
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    profileImageUrl?: string;
  };
}

function shortName(name: string | null | undefined): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  return parts[0][0] + '. ' + parts[Math.ceil(parts.length / 2)];
}

const LDV_AUTO_APPROVAL_HOURS = 3;

function formatDeadlineTimeLdv(createdAt: string | null | undefined): string | null {
  if (!createdAt) return null;
  const deadline = new Date(new Date(createdAt).getTime() + LDV_AUTO_APPROVAL_HOURS * 60 * 60 * 1000);
  if (deadline <= new Date()) return null;
  return deadline.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit", hour12: true });
}

export function LeagueDetailView({ leagueId, onClose, initialTab, initialEventosSubTab, onTabChange }: LeagueDetailViewProps) {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [copiedInvite, setCopiedInvite] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    imageUrl: "",
    isPrivate: true,
  });
  
  // Match edit/delete state
  const [selectedMatch, setSelectedMatch] = useState<LeagueMatch | null>(null);
  const [showEditMatchModal, setShowEditMatchModal] = useState(false);
  const [showDeleteMatchConfirm, setShowDeleteMatchConfirm] = useState(false);
  const [editMatchForm, setEditMatchForm] = useState({
    team1Score: 0,
    team2Score: 0,
    winner: "team1" as "team1" | "team2",
  });
  
  // Member management state
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ id: string; name: string } | null>(null);
  const [pendingFriendRequests, setPendingFriendRequests] = useState<Set<string>>(new Set());
  const [acceptedFriends, setAcceptedFriends] = useState<Set<string>>(new Set());
  
  // Seasons state
  const [showNewSeasonConfirm, setShowNewSeasonConfirm] = useState(false);
  const [showEndSeasonConfirm, setShowEndSeasonConfirm] = useState(false);
  const [selectedPastSeason, setSelectedPastSeason] = useState<LeagueSeason | null>(null);
  const [showSeasonsOnboarding, setShowSeasonsOnboarding] = useState(false);
  const [newSeasonRatingMode, setNewSeasonRatingMode] = useState<"skill_based" | "equal">("skill_based");
  const [showRatingModeHelp, setShowRatingModeHelp] = useState(false);
  const [activeTab, setActiveTabInternal] = useState(initialTab || "standings");
  const [callupSubTab, setCallupSubTab] = useState<"upcoming" | "past">("upcoming");
  const [eventosSubTab, setEventosSubTab] = useState<"convocatorias" | "americanos" | "torneos">(initialEventosSubTab || "convocatorias");

  // If a deep link arrives while this view is already mounted (e.g. a
  // tournament notification tapped with the league already open), honor it.
  useEffect(() => {
    if (initialEventosSubTab) setEventosSubTab(initialEventosSubTab);
  }, [initialEventosSubTab, leagueId]);
  
  const setActiveTab = (tab: string) => {
    setActiveTabInternal(tab);
    onTabChange?.(tab);
  };
  
  // Match summary drawer state
  const [showMatchSummaryDrawer, setShowMatchSummaryDrawer] = useState(false);
  const [matchForSummary, setMatchForSummary] = useState<LeagueMatch | null>(null);
  const [showEditMatchDialog, setShowEditMatchDialog] = useState(false);
  const [matchToEdit, setMatchToEdit] = useState<any>(null);
  const [scheduledEditTarget, setScheduledEditTarget] =
    useState<LeagueScheduledMatch | null>(null);
  const [scheduledResultTarget, setScheduledResultTarget] =
    useState<LeagueScheduledMatch | null>(null);
  const [scheduledLockedTarget, setScheduledLockedTarget] =
    useState<LeagueScheduledMatch | null>(null);
  const [scheduledDeleteTarget, setScheduledDeleteTarget] =
    useState<LeagueScheduledMatch | null>(null);
  const [scheduledDetailTargetId, setScheduledDetailTargetId] = useState<
    string | null
  >(null);


  // Check if user has seen seasons onboarding
  useEffect(() => {
    if (activeTab === "seasons") {
      const hasSeenOnboarding = localStorage.getItem("courtvibe_seasons_onboarding_seen");
      if (!hasSeenOnboarding) {
        setShowSeasonsOnboarding(true);
      }
    }
  }, [activeTab]);

  const dismissSeasonsOnboarding = () => {
    localStorage.setItem("courtvibe_seasons_onboarding_seen", "true");
    setShowSeasonsOnboarding(false);
  };
  
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const { data, isLoading, error } = useQuery<LeagueData>({
    queryKey: ['/api/leagues', leagueId.toString()],
  });

  // Fetch sent friend requests to sync pending state
  const { data: sentRequests } = useQuery<Array<{ addresseeId: string; status: string }>>({
    queryKey: ['/api/friends/sent-pending'],
  });

  // Fetch accepted friends
  const { data: friends } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['/api/friends'],
  });

  // Sync pending friend requests and accepted friends from server
  useEffect(() => {
    if (sentRequests && Array.isArray(sentRequests)) {
      const pendingIds = new Set(
        sentRequests
          .filter((req: any) => req.status === 'pending')
          .map((req: any) => req.addresseeId)
      );
      setPendingFriendRequests(pendingIds);
    }
  }, [sentRequests]);

  useEffect(() => {
    if (friends && Array.isArray(friends)) {
      const friendIds = new Set(friends.map((f: any) => f.id));
      setAcceptedFriends(friendIds);
    }
  }, [friends]);

  // Determine if user is admin/owner before fetching join requests
  const canManageJoinRequests = data?.userMembership?.role === "owner" || data?.userMembership?.role === "admin";
  const isPublicLeague = data?.league?.isPrivate === false;

  // Only fetch join requests if user is admin/owner and league is public
  const { data: joinRequests, isLoading: joinRequestsLoading, error: joinRequestsError } = useQuery<JoinRequest[]>({
    queryKey: ['/api/leagues', leagueId.toString(), 'join-requests'],
    queryFn: async () => {
      const response = await fetch(`/api/leagues/${leagueId}/join-requests?status=pending`, {
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 403) {
          return [];
        }
        throw new Error('Error al obtener solicitudes');
      }
      return response.json();
    },
    enabled: canManageJoinRequests && isPublicLeague,
  });

  const processJoinRequestMutation = useMutation({
    mutationFn: async ({ requestId, status }: { requestId: string; status: "approved" | "rejected" }) => {
      const response = await apiRequest("PATCH", `/api/leagues/${leagueId}/join-requests/${requestId}`, { status });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo procesar la solicitud");
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues'], exact: true });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString(), 'join-requests'] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString(), 'members'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
      toast({
        title: variables.status === "approved" ? "Solicitud aprobada" : "Solicitud rechazada",
        description: variables.status === "approved" 
          ? "El jugador ahora es miembro de la liga" 
          : "La solicitud ha sido rechazada",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo procesar la solicitud",
        variant: "destructive",
      });
    },
  });

  // Seasons query - lazy load only when tab is active
  const { data: seasons, isLoading: seasonsLoading } = useQuery<LeagueSeason[]>({
    queryKey: ['/api/leagues', leagueId.toString(), 'seasons'],
    queryFn: async () => {
      const response = await fetch(`/api/leagues/${leagueId}/seasons`, {
        credentials: 'include',
      });
      if (!response.ok) return [];
      return response.json();
    },
    enabled: activeTab === "seasons",
  });

  const { data: callups = [], isLoading: callupsLoading } = useListLeagueCallups(
    leagueId.toString(),
    undefined,
    { query: { queryKey: getListLeagueCallupsQueryKey(leagueId.toString()), enabled: activeTab === "eventos" } },
  );


  // Get past season rankings when selected
  const { data: pastSeasonData, isLoading: pastSeasonLoading } = useQuery<{ season: LeagueSeason; members: SeasonMember[] }>({
    queryKey: ['/api/leagues', leagueId.toString(), 'seasons', selectedPastSeason?.id],
    queryFn: async () => {
      if (!selectedPastSeason) return null;
      const response = await fetch(`/api/leagues/${leagueId}/seasons/${selectedPastSeason.id}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Error al obtener temporada');
      return response.json();
    },
    enabled: !!selectedPastSeason,
  });

  const startNewSeasonMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/leagues/${leagueId}/seasons`, {
        ratingInitMode: newSeasonRatingMode,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo iniciar la temporada");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString(), 'seasons'] });
      setShowNewSeasonConfirm(false);
      toast({
        title: "Nueva temporada iniciada",
        description: "Los ratings han sido reiniciados y las clasificaciones anteriores guardadas",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo iniciar la temporada",
        variant: "destructive",
      });
    },
  });

  const endSeasonMutation = useMutation({
    mutationFn: async (seasonId: string) => {
      const response = await apiRequest("POST", `/api/leagues/${leagueId}/seasons/${seasonId}/end`, {});
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo terminar la temporada");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString(), 'seasons'] });
      setShowEndSeasonConfirm(false);
      toast({
        title: "Temporada terminada",
        description: "Las clasificaciones finales han sido guardadas",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo terminar la temporada",
        variant: "destructive",
      });
    },
  });

  const updateLeagueMutation = useMutation({
    mutationFn: async (updates: { name: string; description: string; imageUrl: string; isPrivate: boolean }) => {
      const response = await apiRequest("PATCH", `/api/leagues/${leagueId}`, updates);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo actualizar la liga");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      setShowEditModal(false);
      toast({
        title: "Liga actualizada",
        description: "Los cambios han sido guardados",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo actualizar la liga",
        variant: "destructive",
      });
    },
  });

  const deleteLeagueMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/leagues/${leagueId}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo eliminar la liga");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues'] });
      toast({
        title: "Liga eliminada",
        description: "La liga ha sido eliminada correctamente",
      });
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar la liga",
        variant: "destructive",
      });
    },
  });

  const updateMatchMutation = useMutation({
    mutationFn: async (data: { matchId: string; team1Score: number; team2Score: number; winner: "team1" | "team2" }) => {
      const response = await apiRequest("PATCH", `/api/leagues/${leagueId}/matches/${data.matchId}`, {
        team1Score: data.team1Score,
        team2Score: data.team2Score,
        winner: data.winner,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo actualizar el partido");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      setShowEditMatchModal(false);
      setSelectedMatch(null);
      toast({
        title: "Partido actualizado",
        description: "El resultado ha sido actualizado y los rankings recalculados",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo actualizar el partido",
        variant: "destructive",
      });
    },
  });

  const deleteScheduledMatchMutation = useMutation({
    mutationFn: async (matchId: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/scheduled-matches/${matchId}`,
      );
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Error desconocido" }));
        throw new Error(
          errorData.message || "No se pudo eliminar el partido",
        );
      }
      return response.json().catch(() => ({}));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/leagues", leagueId.toString()],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/notifications/unread-count"],
      });
      setScheduledDeleteTarget(null);
      toast({
        title: "Partido eliminado",
        description:
          "Notificamos a los demás jugadores de la cancelación.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "No se pudo eliminar el partido",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMatchMutation = useMutation({
    mutationFn: async (matchId: string) => {
      const response = await apiRequest("DELETE", `/api/leagues/${leagueId}/matches/${matchId}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo eliminar el partido");
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate all relevant caches comprehensively
      // League-related caches
      queryClient.invalidateQueries({ queryKey: ['/api/leagues'] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      // User profile and stats (use correct auth endpoint)
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/profile'] });
      queryClient.invalidateQueries({ queryKey: ['/api/profile/match-history'] });
      queryClient.invalidateQueries({ queryKey: ['/api/profile/level-history'] });
      // Pending matches (in case we deleted a pending match)
      queryClient.invalidateQueries({ queryKey: ['/api/matches/pending'] });
      queryClient.invalidateQueries({ queryKey: ['/api/matches/global/pending'] });
      // Scheduled matches
      queryClient.invalidateQueries({ queryKey: ['/api/scheduled-matches'] });
      
      setShowDeleteMatchConfirm(false);
      setSelectedMatch(null);
      toast({
        title: "Partido eliminado",
        description: "El partido ha sido eliminado y los niveles recalculados",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el partido",
        variant: "destructive",
      });
    },
  });

  const promoteMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const response = await apiRequest("POST", `/api/leagues/${leagueId}/members/${memberId}/promote`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo promover al miembro");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      toast({
        title: "Miembro promovido",
        description: "El miembro ahora es administrador de la liga",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo promover al miembro",
        variant: "destructive",
      });
    },
  });

  const demoteMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const response = await apiRequest("POST", `/api/leagues/${leagueId}/members/${memberId}/demote`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo degradar al administrador");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      toast({
        title: "Administrador degradado",
        description: "El administrador ahora es un miembro regular",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo degradar al administrador",
        variant: "destructive",
      });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const response = await apiRequest("DELETE", `/api/leagues/${leagueId}/members/${memberId}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo eliminar al miembro");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      toast({
        title: "Miembro eliminado",
        description: "El miembro ha sido eliminado de la liga",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar al miembro",
        variant: "destructive",
      });
    },
  });

  const leaveLeagueMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/leagues/${leagueId}/leave`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo abandonar la liga");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues'] });
      toast({
        title: "Has abandonado la liga",
        description: "Ya no eres miembro de esta liga",
      });
      setLocation('/leagues');
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo abandonar la liga",
        variant: "destructive",
      });
    },
  });

  const approveMatchMutation = useMutation({
    mutationFn: async ({ matchId, action }: { matchId: string; action: "approve" | "reject" }) => {
      const response = await apiRequest("POST", `/api/leagues/${leagueId}/matches/${matchId}/approve`, { action });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo procesar el partido");
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString(), 'matches'] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString(), 'members'] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues'] });
      queryClient.invalidateQueries({ queryKey: ['/api/matches/pending'] });
      queryClient.invalidateQueries({ queryKey: ['/api/matches/global/pending'] });
      queryClient.invalidateQueries({ queryKey: ['/api/profile/match-history'] });
      queryClient.invalidateQueries({ queryKey: ['/api/profile/level-history'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/activities'] });
      queryClient.invalidateQueries({ queryKey: ['/api/activities/feed'] });
      toast({
        title: variables.action === "approve" ? "Partido confirmado" : "Partido rechazado",
        description: variables.action === "approve" 
          ? "El partido ha sido confirmado y los rankings actualizados"
          : "El partido ha sido rechazado",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo procesar el partido",
        variant: "destructive",
      });
    },
  });

  const approveScheduledMatchMutation = useMutation({
    mutationFn: async (matchId: string) => {
      const response = await apiRequest("POST", `/api/scheduled-matches/${matchId}/approve`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo aprobar el resultado");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['/api/profile/match-history'] });
      queryClient.invalidateQueries({ queryKey: ['/api/activities'] });
      toast({ title: "Resultado confirmado", description: "El partido ha sido confirmado y los rankings actualizados." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "No se pudo confirmar el resultado", variant: "destructive" });
    },
  });

  const rejectScheduledMatchMutation = useMutation({
    mutationFn: async (matchId: string) => {
      const response = await apiRequest("POST", `/api/scheduled-matches/${matchId}/reject`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo rechazar el resultado");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      toast({ title: "Resultado rechazado", description: "Los jugadores deberán reportar el resultado de nuevo." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "No se pudo rechazar el resultado", variant: "destructive" });
    },
  });

  const sendFriendRequestMutation = useMutation({
    mutationFn: async (addresseeId: string) => {
      setPendingFriendRequests(prev => new Set(prev).add(addresseeId));
      await apiRequest("POST", "/api/friends/request", { addresseeId });
      return addresseeId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', leagueId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
      queryClient.invalidateQueries({ queryKey: ['/api/friends/sent-pending'] });
      queryClient.invalidateQueries({ queryKey: ['/api/players/search'] });
      toast({
        title: "¡Solicitud enviada!",
        description: "Se ha enviado la solicitud de amistad.",
      });
    },
    onError: (error, addresseeId) => {
      setPendingFriendRequests(prev => {
        const next = new Set(prev);
        next.delete(addresseeId);
        return next;
      });
      toast({
        title: "Error",
        description: "No se pudo enviar la solicitud de amistad.",
        variant: "destructive",
      });
    },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/upload/league-image', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al subir la imagen');
      }

      const data = await response.json();
      setEditForm({ ...editForm, imageUrl: data.url });
      toast({
        title: "Imagen subida",
        description: "La imagen se ha subido correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo subir la imagen",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const openEditModal = () => {
    // Navigate to the dedicated edit-league page (admins/owners only).
    // The page itself enforces permissions and surfaces the homeClubId
    // and matchFormat fields. The legacy inline modal is preserved
    // below for backwards compatibility but is no longer reachable
    // from the main "Editar Liga" entry point.
    if (data?.league) {
      setLocation(`/leagues/${leagueId}/edit`);
    }
  };

  const handleSaveEdit = () => {
    updateLeagueMutation.mutate(editForm);
  };

  const canEdit = data?.userMembership?.role === "owner" || data?.userMembership?.role === "admin";
  const isOwner = data?.userMembership?.role === "owner";
  const canManageMatches = canEdit;

  const openEditMatchModal = (match: LeagueMatch) => {
    setSelectedMatch(match);
    // Parse score from either JSON object or string format
    let team1Score = 0;
    let team2Score = 0;
    
    if (match.score) {
      if (typeof match.score === 'object' && 'sets' in match.score) {
        // JSONB format: { sets: [{ team1: number, team2: number }] }
        const scoreObj = match.score as { sets: Array<{ team1: number; team2: number }> };
        if (scoreObj.sets && scoreObj.sets.length > 0) {
          team1Score = scoreObj.sets[0].team1;
          team2Score = scoreObj.sets[0].team2;
        }
      } else if (typeof match.score === 'string') {
        // String format: "6-4"
        const scoreMatch = match.score.match(/(\d+)-(\d+)/);
        if (scoreMatch) {
          team1Score = parseInt(scoreMatch[1]);
          team2Score = parseInt(scoreMatch[2]);
        }
      }
    }
    
    setEditMatchForm({
      team1Score,
      team2Score,
      winner: match.winner === 1 ? "team1" : "team2",
    });
    setShowEditMatchModal(true);
  };

  const openDeleteMatchConfirm = (match: LeagueMatch) => {
    setSelectedMatch(match);
    setShowDeleteMatchConfirm(true);
  };

  const handleSaveMatchEdit = () => {
    if (!selectedMatch) return;
    updateMatchMutation.mutate({
      matchId: selectedMatch.id,
      team1Score: editMatchForm.team1Score,
      team2Score: editMatchForm.team2Score,
      winner: editMatchForm.winner,
    });
  };

  const handleDeleteMatch = () => {
    if (!selectedMatch) return;
    deleteMatchMutation.mutate(selectedMatch.id);
  };

  const copyInviteLink = async () => {
    if (data?.league.inviteCode) {
      const inviteUrl = leagueInviteUrl(data.league.inviteCode);
      if (hasNativeCopy()) {
        const r = await nativeCopy(inviteUrl);
        if (!r?.ok) {
          await navigator.clipboard.writeText(inviteUrl).catch(() => {});
        }
      } else {
        await navigator.clipboard.writeText(inviteUrl);
      }
      setCopiedInvite(true);
      toast({
        title: "¡Enlace copiado!",
        description: "Comparte el enlace con tus amigos para invitarlos",
      });
      setTimeout(() => setCopiedInvite(false), 2000);
    }
  };

  const getInitials = (name: string) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "??";
  };

  const truncateName = (name: string, maxLength: number = 20) => {
    if (!name) return "";
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Hoy";
    if (diffDays === 1) return "Ayer";
    if (diffDays < 7) return `Hace ${diffDays} días`;
    
    return date.toLocaleDateString("es-DO", {
      day: "numeric",
      month: "short",
    });
  };

  const formatScore = (score: LeagueMatch['score']): string => {
    if (!score) return "vs";
    if (typeof score === 'string') return score;
    if (typeof score === 'object' && 'sets' in score && score.sets.length > 0) {
      return score.sets.map(s => `${s.team1}-${s.team2}`).join(" ");
    }
    return "vs";
  };

  const getScoreSets = (score: LeagueMatch['score']): { team1: number; team2: number }[] => {
    if (!score) return [];
    if (typeof score === 'string') {
      const parts = score.split(',').map(s => s.trim());
      const sets: { team1: number; team2: number }[] = [];
      for (const part of parts) {
        const match = part.match(/(\d+)-(\d+)/);
        if (match) {
          sets.push({ team1: parseInt(match[1]), team2: parseInt(match[2]) });
        }
      }
      return sets.length > 0 ? sets : [];
    }
    if (typeof score === 'object' && 'sets' in score && score.sets.length > 0) {
      return score.sets;
    }
    return [];
  };

  const calculateTrendingPlayers = (): TrendingPlayer[] => {
    if (!data?.matches || !data?.members) return [];

    const fifteenDaysAgo = new Date();
    fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);

    // Only count APPROVED matches for trending calculation
    const recentMatches = data.matches.filter(m => 
      m.status === "approved" && new Date(m.playedAt) >= fifteenDaysAgo
    );

    const playerChanges: Record<string, { 
      totalChange: number; 
      matchCount: number;
      currentRating: number;
      userName: string;
      userImage?: string;
    }> = {};

    for (const match of recentMatches) {
      if (!match.ratingChanges) continue;

      const processPlayer = (playerId: string, change: RatingChange | undefined, playerName?: string) => {
        if (!change) return;
        if (!playerChanges[playerId]) {
          const member = data.members.find(m => m.userId === playerId);
          playerChanges[playerId] = {
            totalChange: 0,
            matchCount: 0,
            currentRating: member?.rating || 1000,
            userName: playerName || member?.userName || "Jugador",
            userImage: member?.userImage,
          };
        }
        playerChanges[playerId].totalChange += change.delta;
        playerChanges[playerId].matchCount += 1;
      };

      processPlayer(match.team1Player1Id, match.ratingChanges.team1Player1, match.team1Player1Name);
      if (match.team1Player2Id) {
        processPlayer(match.team1Player2Id, match.ratingChanges.team1Player2, match.team1Player2Name);
      }
      processPlayer(match.team2Player1Id, match.ratingChanges.team2Player1, match.team2Player1Name);
      if (match.team2Player2Id) {
        processPlayer(match.team2Player2Id, match.ratingChanges.team2Player2, match.team2Player2Name);
      }
    }

    return Object.entries(playerChanges)
      .map(([userId, data]) => ({
        userId,
        userName: data.userName,
        userImage: data.userImage,
        ratingChange: data.totalChange,
        currentRating: data.currentRating,
        matchesInPeriod: data.matchCount,
      }))
      .sort((a, b) => Math.abs(b.ratingChange) - Math.abs(a.ratingChange))
      .slice(0, 6);
  };

  const trendingPlayers = calculateTrendingPlayers();
  const topGainers = trendingPlayers.filter(p => p.ratingChange > 0).slice(0, 3);
  const topLosers = trendingPlayers.filter(p => p.ratingChange < 0).slice(0, 3);

  // Calculate all-time stats for activity section
  const calculateMostActive = () => {
    if (!data?.members || !data?.matches) return [];
    const approvedMatches = data.matches.filter(m => m.status === "approved" || !m.status);
    
    const playerMatches: Record<string, { count: number; member: LeagueMember }> = {};
    
    for (const match of approvedMatches) {
      [match.team1Player1Id, match.team1Player2Id, match.team2Player1Id, match.team2Player2Id].forEach(playerId => {
        if (!playerId) return;
        if (!playerMatches[playerId]) {
          const member = data.members.find(m => m.userId === playerId);
          if (member) playerMatches[playerId] = { count: 0, member };
        }
        if (playerMatches[playerId]) playerMatches[playerId].count += 1;
      });
    }
    
    return Object.values(playerMatches)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map((item, index) => ({ ...item.member, rank: index + 1, matchesPlayed: item.count }));
  };

  const calculateBestPerformance = () => {
    if (!data?.members || !data?.matches) return [];
    const approvedMatches = data.matches.filter(m => m.status === "approved" || !m.status);
    
    const playerStats: Record<string, { wins: number; total: number; member: LeagueMember }> = {};
    
    for (const match of approvedMatches) {
      const addStats = (playerId: string | undefined) => {
        if (!playerId) return;
        if (!playerStats[playerId]) {
          const member = data.members.find(m => m.userId === playerId);
          if (member) playerStats[playerId] = { wins: 0, total: 0, member };
        }
        if (playerStats[playerId]) {
          playerStats[playerId].total += 1;
          const isTeam1 = playerId === match.team1Player1Id || playerId === match.team1Player2Id;
          if ((isTeam1 && match.winner === 1) || (!isTeam1 && match.winner === 2)) {
            playerStats[playerId].wins += 1;
          }
        }
      };
      addStats(match.team1Player1Id);
      addStats(match.team1Player2Id);
      addStats(match.team2Player1Id);
      addStats(match.team2Player2Id);
    }
    
    return Object.values(playerStats)
      .filter(p => p.total >= 3)
      .map(p => ({ ...p.member, wins: p.wins, total: p.total, winRate: Math.round((p.wins / p.total) * 100) }))
      .sort((a, b) => (b.wins / b.total) - (a.wins / a.total))
      .slice(0, 3);
  };

  const calculateBestPairs = () => {
    if (!data?.matches) return [];
    const approvedMatches = data.matches.filter(m => m.status === "approved" || !m.status);
    
    const pairsStats: Record<string, { wins: number; total: number; p1: any; p2: any }> = {};
    
    for (const match of approvedMatches) {
      const processPair = (p1Id: string | undefined, p2Id: string | undefined, isWinner: boolean) => {
        if (!p1Id || !p2Id) return;
        const pairKey = [p1Id, p2Id].sort().join("-");
        if (!pairsStats[pairKey]) {
          const p1 = data.members.find(m => m.userId === p1Id);
          const p2 = data.members.find(m => m.userId === p2Id);
          if (p1 && p2) pairsStats[pairKey] = { wins: 0, total: 0, p1, p2 };
        }
        if (pairsStats[pairKey]) {
          pairsStats[pairKey].total += 1;
          if (isWinner) pairsStats[pairKey].wins += 1;
        }
      };
      
      processPair(match.team1Player1Id, match.team1Player2Id, match.winner === 1);
      processPair(match.team2Player1Id, match.team2Player2Id, match.winner === 2);
    }
    
    return Object.values(pairsStats)
      .filter(p => p.p1 && p.p2 && p.total >= 2)
      .map(p => ({ ...p, winRate: Math.round((p.wins / p.total) * 100) }))
      .sort((a, b) => {
        const rateA = a.wins / a.total;
        const rateB = b.wins / b.total;
        if (rateB !== rateA) return rateB - rateA;
        return b.total - a.total;
      })
      .slice(0, 3);
  };

  const mostActive = calculateMostActive();
  const bestPerformance = calculateBestPerformance();
  const bestPairs = calculateBestPairs();

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Trophy className="h-12 w-12 text-primary animate-pulse" />
          <p className="text-muted-foreground">Cargando liga...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center px-4">
          <X className="h-12 w-12 text-destructive" />
          <p className="text-muted-foreground">No se pudo cargar la liga</p>
          <Button onClick={onClose}>Volver</Button>
        </div>
      </div>
    );
  }

  const { league, members: rawMembers, matches, userMembership } = data;
  const currentUserId = userMembership?.userId;
  
  // Sort members: active players first (by rating), then inactive players (matchesPlayed === 0) at the end
  const members = [...rawMembers].sort((a, b) => {
    const aActive = (a.matchesPlayed ?? 0) > 0;
    const bActive = (b.matchesPlayed ?? 0) > 0;
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    // Both same status, sort by rating
    return (b.rating ?? 1000) - (a.rating ?? 1000);
  });
  
  const pendingMatches = matches.filter(m => m.status === "pending");
  const approvedMatches = matches.filter(m => m.status === "approved" || !m.status);
  const recentMatches = approvedMatches;
  
  const canApproveMatch = (match: LeagueMatch) => {
    if (!currentUserId || match.status !== "pending") return false;
    const isTeam1 = currentUserId === match.team1Player1Id || currentUserId === match.team1Player2Id;
    const isTeam2 = currentUserId === match.team2Player1Id || currentUserId === match.team2Player2Id;
    const userTeam = isTeam1 ? 1 : isTeam2 ? 2 : null;
    return userTeam !== null && userTeam !== match.reporterTeam;
  };

  return (
    <div className="fixed inset-0 z-50 bg-background overflow-hidden flex flex-col w-full max-w-full">
      <header 
        className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shrink-0"
        style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top, 0px))' }}
      >
        <div className="flex items-center justify-between p-3 sm:p-4 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={onClose}
              className="shrink-0"
              data-testid="button-close-league-detail"
            >
              <X className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-bold flex items-center gap-2 truncate">
                <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span className="truncate">{league.name}</span>
              </h1>
              {league.homeClubName ? (
                <p
                  className="text-xs text-muted-foreground truncate"
                  data-testid="text-league-home-club"
                >
                  {league.homeClubName}
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {members.length} jugadores · {matches.length} partidos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {canEdit && (
              <Button
                size="icon"
                variant="ghost"
                onClick={openEditModal}
                data-testid="button-edit-league"
              >
                <Settings className="h-5 w-5" />
              </Button>
            )}
            {!isOwner && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setShowLeaveConfirm(true)}
                data-testid="button-leave-league"
                title="Abandonar liga"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={copyInviteLink}
              className="gap-1 sm:gap-2 px-2 sm:px-3"
              data-testid="button-copy-invite"
            >
              {copiedInvite ? (
                <>
                  <Check className="h-4 w-4 text-green-500" />
                  <span className="hidden sm:inline">Copiado</span>
                </>
              ) : (
                <>
                  <Share2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Invitar</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 w-full overflow-y-auto overflow-x-hidden">
        <div className="p-3 sm:p-4 space-y-4 sm:space-y-6 safe-area-bottom">
          {/* Join Requests Card for admins of public leagues */}
          {canManageJoinRequests && isPublicLeague && joinRequests && joinRequests.length > 0 && (
            <Card className="border-primary/30 bg-primary/5" data-testid="card-join-requests">
              <CardHeader className="pb-2 px-3 sm:px-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-primary" />
                  Solicitudes de Unión ({joinRequests.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-4 pb-3">
                <div className="space-y-2">
                  {joinRequests.map((request) => {
                    const userName = [request.user.firstName, request.user.lastName].filter(Boolean).join(" ") || "Usuario";
                    return (
                      <div 
                        key={request.id} 
                        className="flex items-center gap-2 sm:gap-3 p-2 rounded-md bg-background"
                        data-testid={`join-request-${request.id}`}
                      >
                        <Avatar className="h-8 w-8 border-2 border-muted shrink-0">
                          <AvatarImage src={request.user.profileImageUrl || undefined} />
                          <AvatarFallback className="text-xs">
                            {userName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "??"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{userName}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(request.createdAt).toLocaleDateString("es-DO", {
                              day: "numeric",
                              month: "short",
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-100"
                            onClick={() => processJoinRequestMutation.mutate({ requestId: request.id, status: "approved" })}
                            disabled={processJoinRequestMutation.isPending}
                            data-testid={`button-approve-${request.id}`}
                          >
                            <CheckCircle className="h-5 w-5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-100"
                            onClick={() => processJoinRequestMutation.mutate({ requestId: request.id, status: "rejected" })}
                            disabled={processJoinRequestMutation.isPending}
                            data-testid={`button-reject-${request.id}`}
                          >
                            <XCircle className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-full">
            <TabsList className="w-full grid grid-cols-5 h-auto">
              <TabsTrigger
                value="standings"
                className="flex-col gap-[3px] py-1.5 px-1 min-h-11 text-[10px] font-normal data-[state=active]:font-medium data-[state=active]:text-primary"
              >
                <ListOrdered className="h-4 w-4" />
                <span>Clasif.</span>
              </TabsTrigger>
              <TabsTrigger
                value="matches"
                className="flex-col gap-[3px] py-1.5 px-1 min-h-11 text-[10px] font-normal data-[state=active]:font-medium data-[state=active]:text-primary"
              >
                <TennisBallIcon className="h-4 w-4" />
                <span>Partidos</span>
              </TabsTrigger>
              <TabsTrigger
                value="trending"
                className="flex-col gap-[3px] py-1.5 px-1 min-h-11 text-[10px] font-normal data-[state=active]:font-medium data-[state=active]:text-primary"
              >
                <ChartColumn className="h-4 w-4" />
                <span>Tend.</span>
              </TabsTrigger>
              <TabsTrigger
                value="eventos"
                className="flex-col gap-[3px] py-1.5 px-1 min-h-11 text-[10px] font-normal data-[state=active]:font-medium data-[state=active]:text-primary"
              >
                <CalendarStarIcon className="h-4 w-4" />
                <span>Eventos</span>
              </TabsTrigger>
              <TabsTrigger
                value="seasons"
                className="flex-col gap-[3px] py-1.5 px-1 min-h-11 text-[10px] font-normal data-[state=active]:font-medium data-[state=active]:text-primary"
              >
                <Trophy className="h-4 w-4" />
                <span>Temp.</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="standings" className="mt-4">
              <Card className="overflow-hidden">
                <CardHeader className="pb-2 px-3 sm:px-6">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Medal className="h-4 w-4 text-primary" />
                    Clasificación
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-hidden">
                  <div className="divide-y">
                    {members.map((member, index) => {
                      const isInactive = (member.matchesPlayed ?? 0) === 0;
                      // Calculate rank only among active players
                      const activeMembers = members.filter(m => (m.matchesPlayed ?? 0) > 0);
                      const activeRank = isInactive ? null : activeMembers.findIndex(m => m.userId === member.userId) + 1;
                      
                      return (
                      <div
                        key={member.id}
                        className={cn(
                          "flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-3 transition-colors",
                          !isInactive && activeRank && activeRank <= 3 && "bg-primary/5",
                          member.userId === data.userMembership.userId && "bg-primary/10",
                          isInactive && "opacity-50"
                        )}
                        data-testid={`standing-row-${member.userId}`}
                      >
                        <div className={cn(
                          "w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm shrink-0",
                          !isInactive && activeRank === 1 && "bg-yellow-500 text-yellow-950",
                          !isInactive && activeRank === 2 && "bg-gray-300 text-gray-800",
                          !isInactive && activeRank === 3 && "bg-orange-400 text-orange-950",
                          (!isInactive && activeRank && activeRank > 3) && "bg-muted text-muted-foreground",
                          isInactive && "bg-muted/50 text-muted-foreground"
                        )}>
                          {isInactive ? "-" : activeRank}
                        </div>
                        <Avatar 
                          className={cn(
                            "h-8 w-8 sm:h-10 sm:w-10 border-2 shrink-0 cursor-pointer hover:opacity-80",
                            isInactive ? "border-muted/50 grayscale" : "border-muted"
                          )}
                          onClick={() => setSelectedPlayerId(member.userId)}
                        >
                          <AvatarImage src={member.userImage} />
                          <AvatarFallback className="text-xs">
                            {getInitials(member.userName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p 
                            className={cn(
                              "font-medium text-sm sm:text-base truncate flex items-center gap-1 sm:gap-2 cursor-pointer hover:text-primary transition-colors",
                              isInactive && "text-muted-foreground"
                            )}
                            onClick={() => setSelectedPlayerId(member.userId)}
                          >
                            <span className="truncate max-w-[15ch]">{member.userName}</span>
                            {isInactive && (
                              <Badge variant="outline" className="text-[10px] sm:text-xs gap-0.5 px-1 sm:px-1.5 shrink-0 border-muted-foreground/30 text-muted-foreground">
                                Inactivo
                              </Badge>
                            )}
                            {member.role === "owner" && (
                              <Badge variant="default" className="text-[10px] sm:text-xs gap-0.5 sm:gap-1 px-1 sm:px-2 shrink-0">
                                <Crown className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                <span className="hidden sm:inline">Dueño</span>
                              </Badge>
                            )}
                            {member.role === "admin" && (
                              <Badge variant="secondary" className="text-[10px] sm:text-xs gap-0.5 sm:gap-1 px-1 sm:px-2 shrink-0">
                                <Shield className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                <span className="hidden sm:inline">Admin</span>
                              </Badge>
                            )}
                          </p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground">
                            {isInactive ? "Sin partidos" : `${member.matchesWon}G - ${member.matchesLost}P`}
                          </p>
                        </div>
                        <div className="flex items-center shrink-0">
                          <div className="text-right">
                            <p className={cn(
                              "font-bold text-base sm:text-lg tabular-nums",
                              isInactive && "text-muted-foreground"
                            )}>{member.rating}</p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">ELO</p>
                          </div>
                          <div className="w-7 sm:w-8 ml-1 sm:ml-2 flex justify-center">
                            {canEdit && member.role !== "owner" ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-7 w-7 sm:h-8 sm:w-8"
                                    data-testid={`button-member-menu-${member.userId}`}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {!acceptedFriends.has(member.userId) && (
                                    <>
                                      {pendingFriendRequests.has(member.userId) ? (
                                        <DropdownMenuItem disabled data-testid={`button-pending-friend-request-${member.userId}`}>
                                          <Clock className="h-4 w-4 mr-2 text-amber-500" />
                                          Pendiente
                                        </DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem
                                          onClick={() => sendFriendRequestMutation.mutate(member.userId)}
                                          disabled={sendFriendRequestMutation.isPending}
                                          data-testid={`button-send-friend-request-${member.userId}`}
                                        >
                                          <UserPlus className="h-4 w-4 mr-2" />
                                          Agregar
                                        </DropdownMenuItem>
                                      )}
                                    </>
                                  )}
                                  {isOwner && member.role === "member" && (
                                    <DropdownMenuItem
                                      onClick={() => promoteMemberMutation.mutate(member.userId)}
                                      disabled={promoteMemberMutation.isPending}
                                      data-testid={`button-promote-${member.userId}`}
                                    >
                                      <ShieldCheck className="h-4 w-4 mr-2" />
                                      Hacer Admin
                                    </DropdownMenuItem>
                                  )}
                                  {isOwner && member.role === "admin" && (
                                    <DropdownMenuItem
                                      onClick={() => demoteMemberMutation.mutate(member.userId)}
                                      disabled={demoteMemberMutation.isPending}
                                      data-testid={`button-demote-${member.userId}`}
                                    >
                                      <UserMinus className="h-4 w-4 mr-2" />
                                      Quitar Admin
                                    </DropdownMenuItem>
                                  )}
                                  {(isOwner || member.role === "member") && (
                                    <DropdownMenuItem
                                      onClick={() => setMemberToRemove({ id: member.userId, name: member.userName })}
                                      className="text-destructive focus:text-destructive"
                                      data-testid={`button-remove-${member.userId}`}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Eliminar de liga
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-7 w-7 sm:h-8 sm:w-8"
                                    data-testid={`button-member-menu-${member.userId}`}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {!acceptedFriends.has(member.userId) && (
                                    <>
                                      {pendingFriendRequests.has(member.userId) ? (
                                        <DropdownMenuItem disabled data-testid={`button-pending-friend-request-${member.userId}`}>
                                          <Clock className="h-4 w-4 mr-2 text-amber-500" />
                                          Pendiente
                                        </DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem
                                          onClick={() => sendFriendRequestMutation.mutate(member.userId)}
                                          disabled={sendFriendRequestMutation.isPending}
                                          data-testid={`button-send-friend-request-${member.userId}`}
                                        >
                                          <UserPlus className="h-4 w-4 mr-2" />
                                          Agregar
                                        </DropdownMenuItem>
                                      )}
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="matches" className="mt-4 space-y-4">
              {(() => {
                const directItems: LeagueMatch[] = (data?.pendingApprovalDirect as LeagueMatch[] | undefined) ?? pendingMatches;
                const scheduledItems: any[] = (data?.pendingApprovalScheduled as any[] | undefined) ?? [];
                const totalCount = directItems.length + scheduledItems.length;
                if (totalCount === 0) return null;
                return (
                  <Card className="border-orange-500/50" data-testid="card-pending-approval">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-orange-600">
                        <AlertCircle className="h-4 w-4" />
                        Pendientes de aprobación ({totalCount})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {directItems.map((match) => {
                        const team1Names = [match.team1Player1Name];
                        if (match.team1Player2Name) team1Names.push(match.team1Player2Name);
                        
                        const team2Names = [match.team2Player1Name];
                        if (match.team2Player2Name) team2Names.push(match.team2Player2Name);

                        const userCanApprove = canApproveMatch(match);
                        const isReporter = currentUserId === match.reportedById;
                        const deadlineTime = formatDeadlineTimeLdv(match.createdAt);

                        const pendingTeam1Change = match.ratingChanges?.team1Player1?.delta || 0;
                        const pendingTeam2Change = match.ratingChanges?.team2Player1?.delta || 0;
                        const scoreSets = getScoreSets(match.score);

                        const wasEdited =
                          !!match.recordedById &&
                          !!match.reportedById &&
                          match.recordedById !== match.reportedById;
                        const editorName = match.reportedByName || null;

                        return (
                          <div
                            key={match.id}
                            className="p-3 sm:p-4 space-y-3 bg-orange-50/50 dark:bg-orange-950/20"
                            data-testid={`pending-match-row-${match.id}`}
                          >
                            <div className="flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(match.playedAt)}
                              </span>
                              <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-100/50">
                                <Clock className="h-3 w-3 mr-1" />
                                Pendiente
                              </Badge>
                              {wasEdited && editorName && (
                                <span
                                  className="flex items-center gap-1 italic"
                                  data-testid={`text-edited-by-${match.id}`}
                                >
                                  <Pencil className="h-3 w-3" />
                                  Editado por {editorName}
                                </span>
                              )}
                              {canManageMatches && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      data-testid={`button-pending-match-options-${match.id}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {!userCanApprove && (
                                      <>
                                        <DropdownMenuItem
                                          onClick={() => approveMatchMutation.mutate({ matchId: match.id, action: "approve" })}
                                          data-testid={`button-admin-approve-match-${match.id}`}
                                        >
                                          <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                          Aprobar partido
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => approveMatchMutation.mutate({ matchId: match.id, action: "reject" })}
                                          className="text-destructive focus:text-destructive"
                                          data-testid={`button-admin-reject-match-${match.id}`}
                                        >
                                          <XCircle className="h-4 w-4 mr-2" />
                                          Rechazar partido
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                    <DropdownMenuItem
                                      onClick={() => openEditMatchModal(match)}
                                      data-testid={`button-admin-edit-pending-match-${match.id}`}
                                    >
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Editar resultado
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => openDeleteMatchConfirm(match)}
                                      className="text-destructive focus:text-destructive"
                                      data-testid={`button-admin-delete-pending-match-${match.id}`}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Eliminar partido
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                            
                            <div className="space-y-2">
                              <div className={cn(
                                "p-2 sm:p-3 rounded-lg border-2 transition-all",
                                match.winner === 1 
                                  ? "border-orange-400 bg-orange-100/50 dark:bg-orange-900/30" 
                                  : "border-muted"
                              )}>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    {match.winner === 1 && (
                                      <Trophy className="h-4 w-4 text-orange-500 shrink-0" />
                                    )}
                                    <p className="font-medium text-sm truncate">
                                      {team1Names.map(shortName).join(" / ")}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                    {pendingTeam1Change !== 0 && (
                                      <span className={cn(
                                        "text-xs font-medium opacity-70",
                                        pendingTeam1Change > 0 ? "text-green-600" : "text-red-600"
                                      )}>
                                        {pendingTeam1Change > 0 ? "+" : ""}{pendingTeam1Change}
                                      </span>
                                    )}
                                    <div className="flex items-center gap-1">
                                      {scoreSets.map((set, idx) => (
                                        <span 
                                          key={idx} 
                                          className={cn(
                                            "font-bold text-base sm:text-lg tabular-nums px-1.5 py-0.5 rounded",
                                            set.team1 > set.team2 ? "bg-orange-200/50 dark:bg-orange-800/30" : ""
                                          )}
                                        >
                                          {set.team1}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className={cn(
                                "p-2 sm:p-3 rounded-lg border-2 transition-all",
                                match.winner === 2 
                                  ? "border-orange-400 bg-orange-100/50 dark:bg-orange-900/30" 
                                  : "border-muted"
                              )}>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    {match.winner === 2 && (
                                      <Trophy className="h-4 w-4 text-orange-500 shrink-0" />
                                    )}
                                    <p className="font-medium text-sm truncate">
                                      {team2Names.map(shortName).join(" / ")}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                    {pendingTeam2Change !== 0 && (
                                      <span className={cn(
                                        "text-xs font-medium opacity-70",
                                        pendingTeam2Change > 0 ? "text-green-600" : "text-red-600"
                                      )}>
                                        {pendingTeam2Change > 0 ? "+" : ""}{pendingTeam2Change}
                                      </span>
                                    )}
                                    <div className="flex items-center gap-1">
                                      {scoreSets.map((set, idx) => (
                                        <span 
                                          key={idx} 
                                          className={cn(
                                            "font-bold text-base sm:text-lg tabular-nums px-1.5 py-0.5 rounded",
                                            set.team2 > set.team1 ? "bg-orange-200/50 dark:bg-orange-800/30" : ""
                                          )}
                                        >
                                          {set.team2}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {userCanApprove ? (
                              <>
                                <div className="flex items-center justify-center gap-2 pt-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700 flex-1 sm:flex-none"
                                    onClick={() => approveMatchMutation.mutate({ matchId: match.id, action: "reject" })}
                                    disabled={approveMatchMutation.isPending}
                                    data-testid={`button-reject-match-${match.id}`}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Rechazar
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                                    onClick={() => approveMatchMutation.mutate({ matchId: match.id, action: "approve" })}
                                    disabled={approveMatchMutation.isPending}
                                    data-testid={`button-approve-match-${match.id}`}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Confirmar
                                  </Button>
                                </div>
                                {match.createdAt ? (
                                  <p className="text-xs text-center text-muted-foreground pt-1">
                                    <Clock className="inline h-3 w-3 mr-1" />
                                    Se aprueba automáticamente a las {formatDeadlineTime(match.createdAt)}
                                  </p>
                                ) : null}
                              </>
                            ) : isReporter ? (
                              <>
                                <div className="flex items-center justify-center gap-2 pt-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 sm:flex-none"
                                    onClick={() => openEditMatchModal(match)}
                                    data-testid={`button-edit-pending-match-${match.id}`}
                                  >
                                    <Pencil className="h-4 w-4 mr-1" />
                                    Editar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700 flex-1 sm:flex-none"
                                    onClick={() => openDeleteMatchConfirm(match)}
                                    data-testid={`button-delete-pending-match-${match.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Eliminar
                                  </Button>
                                </div>
                                {match.createdAt ? (
                                  <p className="text-xs text-center text-muted-foreground pt-1">
                                    <Clock className="inline h-3 w-3 mr-1" />
                                    Se aprueba automáticamente a las {formatDeadlineTime(match.createdAt)}
                                  </p>
                                ) : null}
                              </>
                            ) : (
                              <>
                                <p className="text-xs text-center text-muted-foreground pt-1">
                                  Este partido está pendiente de confirmación
                                </p>
                                {match.createdAt ? (
                                  <p className="text-xs text-center text-muted-foreground pt-1">
                                    <Clock className="inline h-3 w-3 mr-1" />
                                    Se aprueba automáticamente a las {formatDeadlineTime(match.createdAt)}
                                  </p>
                                ) : null}
                              </>
                            )}
                          {deadlineTime && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground mt-2 px-4 pb-2">
                              <Clock className="h-3 w-3 shrink-0" />
                              Se aprueba automáticamente a las {deadlineTime}
                            </p>
                          )}
                          </div>
                        );
                        })}
                        {scheduledItems.map((sm: any) => {
                          const team1Names = [sm.team1Player1?.name, sm.team1Player2?.name].filter(Boolean).join(" / ") || "—";
                          const team2Names = [sm.team2Player1?.name, sm.team2Player2?.name].filter(Boolean).join(" / ") || "—";
                          const submitterTeam = sm.scoreSubmitterTeam as number | null;
                          const isOpposingTeam = !!currentUserId && (
                            (submitterTeam === 1 && (sm.team2Player1Id === currentUserId || sm.team2Player2Id === currentUserId)) ||
                            (submitterTeam === 2 && (sm.team1Player1Id === currentUserId || sm.team1Player2Id === currentUserId))
                          );
                          const userCanConfirm = isOpposingTeam || canEdit;
                          const scoreSets = getScoreSets(sm.score);
                          return (
                            <div key={`scheduled-${sm.id}`} className="p-3 sm:p-4 space-y-3 bg-orange-50/30 dark:bg-orange-950/10" data-testid={`row-pending-approval-${sm.id}`}>
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  Agendado
                                </span>
                                <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-100/50">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pendiente de confirmación
                                </Badge>
                              </div>
                              {scoreSets.length > 0 ? (
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1 min-w-0 flex-1">
                                      {sm.winner === 1 && <Trophy className="h-3.5 w-3.5 text-primary shrink-0" />}
                                      <p className="text-sm font-medium truncate">{team1Names}</p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      {scoreSets.map((set: any, idx: number) => (
                                        <span key={idx} className={cn("font-bold text-sm tabular-nums px-1.5 py-0.5 rounded", set.team1 > set.team2 ? "bg-primary/15 text-primary" : "text-muted-foreground")}>
                                          {set.team1}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1 min-w-0 flex-1">
                                      {sm.winner === 2 && <Trophy className="h-3.5 w-3.5 text-primary shrink-0" />}
                                      <p className="text-sm font-medium truncate">{team2Names}</p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      {scoreSets.map((set: any, idx: number) => (
                                        <span key={idx} className={cn("font-bold text-sm tabular-nums px-1.5 py-0.5 rounded", set.team2 > set.team1 ? "bg-primary/15 text-primary" : "text-muted-foreground")}>
                                          {set.team2}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-sm font-medium">{team1Names}</p>
                                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">vs</p>
                                  <p className="text-sm font-medium">{team2Names}</p>
                                </div>
                              )}
                              {sm.scoreSubmittedByName && (
                                <p className="text-xs text-muted-foreground">
                                  Reportado por: <span className="font-medium">{sm.scoreSubmittedByName}</span>
                                </p>
                              )}
                              {userCanConfirm ? (
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700 flex-1"
                                    onClick={() => rejectScheduledMatchMutation.mutate(sm.id)}
                                    disabled={rejectScheduledMatchMutation.isPending || approveScheduledMatchMutation.isPending}
                                    data-testid={`button-reject-scheduled-${sm.id}`}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Rechazar
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 flex-1"
                                    onClick={() => approveScheduledMatchMutation.mutate(sm.id)}
                                    disabled={approveScheduledMatchMutation.isPending || rejectScheduledMatchMutation.isPending}
                                    data-testid={`button-approve-scheduled-${sm.id}`}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Confirmar
                                  </Button>
                                </div>
                              ) : (
                                <p className="text-xs text-center text-muted-foreground">
                                  Esperando confirmación del equipo contrario
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {data?.scheduledMatches && data.scheduledMatches.length > 0 && (() => {
                const nowMs = Date.now();
                const isPastSchedule = (sm: typeof data.scheduledMatches[number]) => {
                  const ts = Date.parse(
                    `${sm.scheduledDate}T${sm.scheduledTime || "00:00"}:00`,
                  );
                  return Number.isFinite(ts) && ts < nowMs;
                };
                const tsOf = (sm: typeof data.scheduledMatches[number]) =>
                  Date.parse(
                    `${sm.scheduledDate}T${sm.scheduledTime || "00:00"}:00`,
                  ) || 0;
                const pendingResultScheduled = data.pendingResultScheduled
                  ?? data.scheduledMatches
                    .filter(
                      (sm) =>
                        (sm.status === "scheduled" ||
                          sm.status === "score_pending") &&
                        isPastSchedule(sm),
                    )
                    .sort((a, b) => tsOf(b) - tsOf(a));
                const upcomingScheduled = data.upcomingScheduled
                  ?? data.scheduledMatches
                    .filter(
                      (sm) => sm.status === "scheduled" && !isPastSchedule(sm),
                    )
                    .sort((a, b) => tsOf(a) - tsOf(b));
                const visibleCount =
                  pendingResultScheduled.length +
                  upcomingScheduled.length;
                if (visibleCount === 0) return null;
                const cardTitle =
                  pendingResultScheduled.length > 0
                    ? "Partidos agendados"
                    : "Próximos partidos";

                const renderScheduledRow = (sm: typeof data.scheduledMatches[number]) => {
                  const team1Names =
                    [sm.team1Player1?.name, sm.team1Player2?.name]
                      .filter(Boolean)
                      .join(" / ") || "—";
                  const team2Names =
                    [sm.team2Player1?.name, sm.team2Player2?.name]
                      .filter(Boolean)
                      .join(" / ") || "—";
                  let dateLabel = sm.scheduledDate;
                  try {
                    const d = new Date(
                      `${sm.scheduledDate}T${sm.scheduledTime || "00:00"}:00`,
                    );
                    if (!Number.isNaN(d.getTime())) {
                      dateLabel = d.toLocaleDateString("es-DO", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      });
                    }
                  } catch {
                    // keep raw string
                  }

                  const isCompleted = sm.status === "completed";
                  const isSubmitted = sm.status === "score_submitted";
                  const isScorePending = sm.status === "score_pending";

                  // Status visual: distinct icon + tint for each state so the
                  // user can tell things apart without needing to look at the
                  // available action buttons.
                  let StatusIcon = Calendar;
                  let statusLabel = "Agendado";
                  let statusClasses =
                    "border-transparent bg-muted text-muted-foreground";
                  if (isScorePending) {
                    StatusIcon = Clock;
                    statusLabel = "Esperando resultado";
                    statusClasses =
                      "border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
                  } else if (isSubmitted) {
                    StatusIcon = HelpCircle;
                    statusLabel = "Esperando aprobación";
                    statusClasses =
                      "border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
                  } else if (isCompleted) {
                    StatusIcon = CheckCircle;
                    statusLabel = "Resultado registrado";
                    statusClasses =
                      "border-transparent bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
                  }

                  const completedScoreSets = isCompleted
                    ? getScoreSets(sm.score ?? undefined)
                    : [];

                  return (
                    <div
                      key={sm.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (sm.status !== "completed" && isPastSchedule(sm)) {
                          setScheduledResultTarget(sm);
                        } else {
                          setScheduledDetailTargetId(sm.id);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (sm.status !== "completed" && isPastSchedule(sm)) {
                            setScheduledResultTarget(sm);
                          } else {
                            setScheduledDetailTargetId(sm.id);
                          }
                        }
                      }}
                      className="flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid={`row-scheduled-match-${sm.id}`}
                    >
                      <div
                        className={cn(
                          "flex flex-col items-center justify-center rounded-md border px-2 py-1 min-w-[64px]",
                          isCompleted
                            ? "border-muted-foreground/30 bg-muted/40"
                            : "border-primary/40 bg-primary/10",
                        )}
                      >
                        <span
                          className={cn(
                            "text-[10px] font-bold uppercase tracking-wide",
                            isCompleted ? "text-muted-foreground" : "text-primary",
                          )}
                        >
                          {dateLabel}
                        </span>
                        <span className="text-xs font-semibold">
                          {sm.scheduledTime}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <Badge
                            className={cn(
                              "text-[10px] gap-1 px-1.5 py-0.5",
                              statusClasses,
                            )}
                            data-testid={`badge-scheduled-status-${sm.id}`}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {statusLabel}
                          </Badge>
                        </div>

                        {isCompleted && completedScoreSets.length > 0 ? (
                          <div className="space-y-1 mt-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1 min-w-0 flex-1">
                                {sm.winner === 1 && (
                                  <Trophy className="h-3.5 w-3.5 text-primary shrink-0" />
                                )}
                                <p
                                  className="text-sm font-medium truncate"
                                  data-testid={`text-scheduled-team1-${sm.id}`}
                                >
                                  {team1Names}
                                </p>
                              </div>
                              <div
                                className="flex items-center gap-1 shrink-0"
                                data-testid={`scheduled-team1-score-${sm.id}`}
                              >
                                {completedScoreSets.map((set, idx) => (
                                  <span
                                    key={idx}
                                    className={cn(
                                      "font-bold text-sm tabular-nums px-1.5 py-0.5 rounded",
                                      set.team1 > set.team2
                                        ? "bg-primary/15 text-primary"
                                        : "text-muted-foreground",
                                    )}
                                  >
                                    {set.team1}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1 min-w-0 flex-1">
                                {sm.winner === 2 && (
                                  <Trophy className="h-3.5 w-3.5 text-primary shrink-0" />
                                )}
                                <p
                                  className="text-sm font-medium truncate"
                                  data-testid={`text-scheduled-team2-${sm.id}`}
                                >
                                  {team2Names}
                                </p>
                              </div>
                              <div
                                className="flex items-center gap-1 shrink-0"
                                data-testid={`scheduled-team2-score-${sm.id}`}
                              >
                                {completedScoreSets.map((set, idx) => (
                                  <span
                                    key={idx}
                                    className={cn(
                                      "font-bold text-sm tabular-nums px-1.5 py-0.5 rounded",
                                      set.team2 > set.team1
                                        ? "bg-primary/15 text-primary"
                                        : "text-muted-foreground",
                                    )}
                                  >
                                    {set.team2}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p
                              className="text-sm font-medium truncate"
                              data-testid={`text-scheduled-team1-${sm.id}`}
                            >
                              {team1Names}
                            </p>
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              vs
                            </p>
                            <p
                              className="text-sm font-medium truncate"
                              data-testid={`text-scheduled-team2-${sm.id}`}
                            >
                              {team2Names}
                            </p>
                          </>
                        )}

                        <p className="text-xs text-muted-foreground truncate mt-1">
                          {sm.homeClubName ? `${sm.homeClubName} · ` : ""}
                          {sm.location}
                          {sm.court ? ` · ${sm.court}` : ""}
                        </p>
                      </div>
                      {(() => {
                        const isAssignedPlayer =
                          !!currentUserId &&
                          (sm.team1Player1Id === currentUserId ||
                            sm.team1Player2Id === currentUserId ||
                            sm.team2Player1Id === currentUserId ||
                            sm.team2Player2Id === currentUserId);
                        if (!canEdit && !isAssignedPlayer) return null;
                        const locked = isScheduledMatchLocked(
                          sm.scheduledDate,
                          sm.scheduledTime,
                        );
                        const isOverride =
                          sm.status === "completed" ||
                          sm.status === "score_submitted";
                        const canDelete =
                          sm.status === "scheduled" ||
                          sm.status === "score_pending";
                        return (
                          <div
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {locked ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setScheduledLockedTarget(sm);
                                }}
                                className="relative text-muted-foreground"
                                data-testid={`button-register-result-scheduled-${sm.id}`}
                                aria-label="Registrar resultado (bloqueado)"
                              >
                                <Trophy className="h-4 w-4 mr-1 opacity-50" />
                                Resultado
                                <span
                                  className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-zinc-900 text-white flex items-center justify-center border border-background"
                                  aria-hidden="true"
                                >
                                  <Lock className="h-2.5 w-2.5" />
                                </span>
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setScheduledResultTarget(sm);
                                }}
                                data-testid={`button-register-result-scheduled-${sm.id}`}
                                aria-label={
                                  isOverride
                                    ? "Corregir resultado"
                                    : "Registrar resultado"
                                }
                              >
                                <Trophy className="h-4 w-4 mr-1" />
                                {isOverride ? "Corregir" : "Resultado"}
                              </Button>
                            )}
                            {sm.status === "scheduled" && (
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setScheduledEditTarget(sm);
                                }}
                                data-testid={`button-edit-scheduled-${sm.id}`}
                                aria-label="Editar partido"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setScheduledDeleteTarget(sm);
                                }}
                                data-testid={`button-delete-scheduled-${sm.id}`}
                                aria-label="Eliminar partido"
                                className="text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                };

                return (
                  <Card data-testid="card-scheduled-matches">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        {cardTitle} ({visibleCount})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {pendingResultScheduled.length > 0 && (
                          <div
                            className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900/60 dark:bg-amber-950/30"
                            data-testid="section-scheduled-pending-result"
                          >
                            <div className="flex items-center gap-2 text-amber-900 dark:text-amber-200">
                              <AlertCircle className="h-4 w-4" />
                              <p className="text-xs font-semibold uppercase tracking-wide">
                                Pendientes de resultado ({pendingResultScheduled.length})
                              </p>
                            </div>
                            <p className="text-xs text-amber-900/80 dark:text-amber-200/80">
                              Estos partidos ya pasaron y todavía no tienen resultado. Reporta el score para que cuente en la liga.
                            </p>
                            {pendingResultScheduled.map(renderScheduledRow)}
                          </div>
                        )}
                        {upcomingScheduled.length > 0 && (
                          <div
                            className="space-y-2"
                            data-testid="section-scheduled-upcoming"
                          >
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Próximos partidos ({upcomingScheduled.length})
                            </p>
                            {upcomingScheduled.map(renderScheduledRow)}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              <Card>
                <CardHeader className="pb-2 flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Swords className="h-4 w-4 text-primary" />
                    Partidos Recientes
                  </CardTitle>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setLocation(`/registrar-partido?leagueId=${leagueId}`)}
                        className="text-xs"
                        data-testid="button-admin-record-match"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Registrar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setLocation(`/agendar-partido?leagueId=${leagueId}`)}
                        className="text-xs"
                        data-testid="button-admin-schedule-match"
                      >
                        <Calendar className="h-3.5 w-3.5 mr-1" />
                        Agendar
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-0 overflow-hidden">
                  {recentMatches.length === 0 ? (
                    <div className="p-8 text-center">
                      <Swords className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No hay partidos registrados aún
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {recentMatches.map((match) => {
                        const team1Names = [match.team1Player1Name];
                        if (match.team1Player2Name) team1Names.push(match.team1Player2Name);
                        
                        const team2Names = [match.team2Player1Name];
                        if (match.team2Player2Name) team2Names.push(match.team2Player2Name);

                        const team1Change = match.ratingChanges?.team1Player1?.delta || 0;
                        const team2Change = match.ratingChanges?.team2Player1?.delta || 0;
                        const scoreSets = getScoreSets(match.score);

                        return (
                          <div
                            key={match.id}
                            className="p-3 sm:p-4 space-y-2 cursor-pointer hover:bg-muted/50 transition-colors"
                            data-testid={`match-row-${match.id}`}
                            onClick={() => {
                              setMatchForSummary(match);
                              setShowMatchSummaryDrawer(true);
                            }}
                          >
                            <div className="flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
                              <span className="flex items-center gap-2 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(match.playedAt)}
                                </span>
                                {match.homeClubName && (
                                  <span
                                    className="flex items-center gap-1"
                                    data-testid={`match-club-${match.id}`}
                                  >
                                    <MapPin className="h-3 w-3" />
                                    {match.homeClubName}
                                  </span>
                                )}
                              </span>
                              <div className="flex items-center gap-2">
                                {match.notes && (
                                  <Badge variant="outline" className="text-xs">
                                    {match.notes}
                                  </Badge>
                                )}
                                {canManageMatches && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        data-testid={`button-match-options-${match.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        onClick={() => openEditMatchModal(match)}
                                        data-testid={`button-edit-match-${match.id}`}
                                      >
                                        <Pencil className="h-4 w-4 mr-2" />
                                        Editar resultado
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => openDeleteMatchConfirm(match)}
                                        className="text-destructive focus:text-destructive"
                                        data-testid={`button-delete-match-${match.id}`}
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Eliminar partido
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <div className={cn(
                                "p-2 sm:p-3 rounded-lg border-2 transition-all",
                                match.winner === 1 
                                  ? "border-green-500 bg-green-500/10" 
                                  : "border-muted"
                              )}>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    {match.winner === 1 && (
                                      <Trophy className="h-4 w-4 text-green-500 shrink-0" />
                                    )}
                                    <p className="font-medium text-sm truncate">
                                      {team1Names.map(shortName).join(" / ")}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                    {team1Change !== 0 && (
                                      <span className={cn(
                                        "text-xs font-medium",
                                        team1Change > 0 ? "text-green-600" : "text-red-600"
                                      )}>
                                        {team1Change > 0 ? "+" : ""}{team1Change}
                                      </span>
                                    )}
                                    <div className="flex items-center gap-1">
                                      {scoreSets.map((set, idx) => (
                                        <span 
                                          key={idx} 
                                          className={cn(
                                            "font-bold text-base sm:text-lg tabular-nums px-1.5 py-0.5 rounded",
                                            set.team1 > set.team2 ? "bg-green-200/50 dark:bg-green-800/30" : ""
                                          )}
                                        >
                                          {set.team1}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className={cn(
                                "p-2 sm:p-3 rounded-lg border-2 transition-all",
                                match.winner === 2 
                                  ? "border-green-500 bg-green-500/10" 
                                  : "border-muted"
                              )}>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    {match.winner === 2 && (
                                      <Trophy className="h-4 w-4 text-green-500 shrink-0" />
                                    )}
                                    <p className="font-medium text-sm truncate">
                                      {team2Names.map(shortName).join(" / ")}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                    {team2Change !== 0 && (
                                      <span className={cn(
                                        "text-xs font-medium",
                                        team2Change > 0 ? "text-green-600" : "text-red-600"
                                      )}>
                                        {team2Change > 0 ? "+" : ""}{team2Change}
                                      </span>
                                    )}
                                    <div className="flex items-center gap-1">
                                      {scoreSets.map((set, idx) => (
                                        <span 
                                          key={idx} 
                                          className={cn(
                                            "font-bold text-base sm:text-lg tabular-nums px-1.5 py-0.5 rounded",
                                            set.team2 > set.team1 ? "bg-green-200/50 dark:bg-green-800/30" : ""
                                          )}
                                        >
                                          {set.team2}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="trending" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    Cambios Recientes (Últimos 15 días)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {trendingPlayers.length === 0 ? (
                    <div className="p-8 text-center">
                      <TrendingUp className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No hay actividad reciente
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Registra partidos para ver tendencias
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {topGainers.length > 0 && (
                        <div className="space-y-3">
                          <h3 className="text-sm font-medium flex items-center gap-2 text-green-600 dark:text-green-400">
                            <TrendingUp className="h-4 w-4" />
                            En Racha
                          </h3>
                          <div className="space-y-2">
                            {topGainers.map((player, index) => (
                              <div
                                key={player.userId}
                                className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20"
                              >
                                <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">
                                  {index + 1}
                                </div>
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={player.userImage} />
                                  <AvatarFallback className="text-xs">
                                    {getInitials(player.userName)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{truncateName(player.userName)}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {player.matchesInPeriod} partidos
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-green-600 dark:text-green-400 flex items-center gap-1">
                                    <ArrowUp className="h-4 w-4" />
                                    +{player.ratingChange}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {player.currentRating} ELO
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {topLosers.length > 0 && (
                        <div className="space-y-3">
                          <h3 className="text-sm font-medium flex items-center gap-2 text-red-600 dark:text-red-400">
                            <TrendingDown className="h-4 w-4" />
                            En Bajada
                          </h3>
                          <div className="space-y-2">
                            {topLosers.map((player, index) => (
                              <div
                                key={player.userId}
                                className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20"
                              >
                                <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold">
                                  {index + 1}
                                </div>
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={player.userImage} />
                                  <AvatarFallback className="text-xs">
                                    {getInitials(player.userName)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{truncateName(player.userName)}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {player.matchesInPeriod} partidos
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-red-600 dark:text-red-400 flex items-center gap-1">
                                    <ArrowDown className="h-4 w-4" />
                                    {player.ratingChange}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {player.currentRating} ELO
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {topGainers.length === 0 && topLosers.length === 0 && trendingPlayers.length > 0 && (
                        <div className="space-y-3">
                          <h3 className="text-sm font-medium flex items-center gap-2">
                            <Minus className="h-4 w-4" />
                            Estables
                          </h3>
                          <div className="space-y-2">
                            {trendingPlayers.map((player) => (
                              <div
                                key={player.userId}
                                className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                              >
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={player.userImage} />
                                  <AvatarFallback className="text-xs">
                                    {getInitials(player.userName)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{truncateName(player.userName)}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {player.matchesInPeriod} partidos
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium text-muted-foreground">
                                    {player.ratingChange === 0 ? "±0" : player.ratingChange}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {player.currentRating} ELO
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    Resumen de Actividad
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-muted/50 text-center">
                      <p className="text-3xl font-bold text-primary">
                        {approvedMatches.length}
                      </p>
                      <p className="text-xs text-muted-foreground">Partidos Totales</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50 text-center">
                      <p className="text-3xl font-bold text-primary">
                        {new Set(approvedMatches.flatMap(m => [m.team1Player1Id, m.team1Player2Id, m.team2Player1Id, m.team2Player2Id].filter(Boolean))).size}
                      </p>
                      <p className="text-xs text-muted-foreground">Jugadores Activos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Most Active Players - All Time */}
              {mostActive.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      Jugadores Más Activos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {mostActive.map((player) => (
                        <div
                          key={player.userId}
                          className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                          data-testid={`card-active-${player.userId}`}
                        >
                          <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                            {player.rank}
                          </div>
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={player.userImage} />
                            <AvatarFallback className="text-xs">
                              {getInitials(player.userName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{truncateName(player.userName)}</p>
                            <p className="text-xs text-muted-foreground">
                              {player.matchesPlayed || player.matchesPlayed} partidos
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Best Performance - Win Rate */}
              {bestPerformance.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-yellow-500" />
                      Mejor Desempeño
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {bestPerformance.map((player, index) => (
                        <div
                          key={player.userId}
                          className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                          data-testid={`card-performance-${player.userId}`}
                        >
                          <div className={cn(
                            "w-6 h-6 rounded-full text-white flex items-center justify-center text-xs font-bold",
                            index === 0 && "bg-yellow-500",
                            index === 1 && "bg-gray-400",
                            index === 2 && "bg-amber-700"
                          )}>
                            {index + 1}
                          </div>
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={player.userImage} />
                            <AvatarFallback className="text-xs">
                              {getInitials(player.userName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{truncateName(player.userName)}</p>
                            <p className="text-xs text-muted-foreground">
                              {player.wins}W-{player.total - player.wins}L
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-yellow-600 dark:text-yellow-500">
                              {player.winRate}%
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Best Pairs - Doubles */}
              {bestPairs.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Swords className="h-4 w-4 text-green-600 dark:text-green-400" />
                      Mejores Duplas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {bestPairs.map((pair, index) => (
                        <div
                          key={`${pair.p1.userId}-${pair.p2.userId}`}
                          className="flex flex-col xs:flex-row items-start xs:items-center gap-2 p-3 rounded-lg bg-muted/50"
                          data-testid={`card-pair-${pair.p1.userId}-${pair.p2.userId}`}
                        >
                          <div className="flex items-center gap-2 w-full xs:w-auto">
                            <div className="w-6 h-6 shrink-0 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </div>
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={pair.p1.userImage} />
                                <AvatarFallback className="text-xs">
                                  {getInitials(pair.p1.userName)}
                                </AvatarFallback>
                              </Avatar>
                              <p className="text-xs font-medium truncate max-w-[80px] sm:max-w-none">{truncateName(pair.p1.userName)}</p>
                              <span className="text-xs text-muted-foreground">+</span>
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={pair.p2.userImage} />
                                <AvatarFallback className="text-xs">
                                  {getInitials(pair.p2.userName)}
                                </AvatarFallback>
                              </Avatar>
                              <p className="text-xs font-medium truncate max-w-[80px] sm:max-w-none">{truncateName(pair.p2.userName)}</p>
                            </div>
                          </div>
                          <div className="flex xs:flex-col items-center xs:items-end justify-between w-full xs:w-auto pt-2 xs:pt-0 border-t xs:border-0 border-muted-foreground/10">
                            <p className="font-bold text-green-600 dark:text-green-400 text-sm">
                              {pair.winRate}% <span className="xs:hidden text-muted-foreground font-normal ml-1">Win Rate</span>
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {pair.wins}-{pair.total - pair.wins} <span className="xs:hidden ml-1">W/L</span>
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="seasons" className="mt-4 space-y-4">
              {seasonsLoading ? (
                <Card>
                  <CardContent className="p-8 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </CardContent>
                </Card>
              ) : (
              <>
              {/* Current Season / Start New Season */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      Temporada Actual
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setShowSeasonsOnboarding(true)}
                        data-testid="button-seasons-help"
                      >
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                    {(userMembership?.role === "owner" || userMembership?.role === "admin") && (
                      <div className="flex gap-2">
                        {seasons && seasons.find(s => s.status === "active") ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowEndSeasonConfirm(true)}
                              className="gap-1"
                              data-testid="button-end-season"
                            >
                              <XCircle className="h-4 w-4" />
                              <span className="hidden sm:inline">Terminar</span>
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setNewSeasonRatingMode(data?.league?.ratingInitMode === "equal" ? "equal" : "skill_based");
                                setShowNewSeasonConfirm(true);
                              }}
                              className="gap-1"
                              data-testid="button-new-season"
                            >
                              <Calendar className="h-4 w-4" />
                              <span className="hidden sm:inline">Nueva</span>
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => {
                              setNewSeasonRatingMode(data?.league?.ratingInitMode === "equal" ? "equal" : "skill_based");
                              setShowNewSeasonConfirm(true);
                            }}
                            className="gap-1"
                            data-testid="button-start-season"
                          >
                            <Calendar className="h-4 w-4" />
                            Iniciar Temporada
                          </Button>
                        )}
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {seasons && seasons.find(s => s.status === "active") ? (
                    <div className="space-y-2">
                      {(() => {
                        const activeSeason = seasons.find(s => s.status === "active");
                        return activeSeason && (
                          <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-primary">{activeSeason.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  Iniciada el {new Date(activeSeason.startedAt).toLocaleDateString('es-ES', { 
                                    day: 'numeric', 
                                    month: 'long', 
                                    year: 'numeric' 
                                  })}
                                </p>
                              </div>
                              <Badge variant="default" className="gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Activa
                              </Badge>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <Calendar className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No hay temporada activa
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {userMembership?.role === "owner" || userMembership?.role === "admin" 
                          ? "Inicia una nueva temporada para reiniciar los ratings"
                          : "El administrador puede iniciar una nueva temporada"
                        }
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Past Seasons */}
              {seasons && seasons.filter(s => s.status === "ended").length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-muted-foreground" />
                      Temporadas Anteriores
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {seasons.filter(s => s.status === "ended").map((season) => (
                        <div
                          key={season.id}
                          className="p-4 hover-elevate cursor-pointer"
                          onClick={() => setSelectedPastSeason(season)}
                          data-testid={`season-${season.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{season.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(season.startedAt).toLocaleDateString('es-ES', { 
                                  day: 'numeric', 
                                  month: 'short', 
                                  year: 'numeric' 
                                })}
                                {season.endedAt && (
                                  <> - {new Date(season.endedAt).toLocaleDateString('es-ES', { 
                                    day: 'numeric', 
                                    month: 'short', 
                                    year: 'numeric' 
                                  })}</>
                                )}
                              </p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              Ver rankings
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* No seasons message */}
              {(!seasons || seasons.length === 0) && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Trophy className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      No hay historial de temporadas
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Inicia una temporada para comenzar a rastrear el historial
                    </p>
                  </CardContent>
                </Card>
              )}
              </>
              )}
            </TabsContent>

            <TabsContent value="eventos" className="mt-4 space-y-4">
              {/* Sub-navigation: Convocatorias | Americanos | Torneos */}
              <div className="flex gap-1 rounded-md bg-muted p-1 w-fit">
                <Button
                  type="button"
                  size="sm"
                  variant={eventosSubTab === "convocatorias" ? "default" : "ghost"}
                  className="h-7 px-3"
                  onClick={() => setEventosSubTab("convocatorias")}
                  data-testid="button-eventos-subtab-convocatorias"
                >
                  Convocatorias
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={eventosSubTab === "americanos" ? "default" : "ghost"}
                  className="h-7 px-3"
                  onClick={() => setEventosSubTab("americanos")}
                  data-testid="button-eventos-subtab-americanos"
                >
                  Americanos
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={eventosSubTab === "torneos" ? "default" : "ghost"}
                  className="h-7 px-3"
                  onClick={() => setEventosSubTab("torneos")}
                  data-testid="button-eventos-subtab-torneos"
                >
                  Torneos
                </Button>
              </div>

              {/* ---- Convocatorias ---- */}
              {eventosSubTab === "convocatorias" && (
                <>
                  {canManageMatches && (
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        className="gap-1"
                        onClick={() => setLocation(`/leagues/${leagueId}/crear-convocatoria`)}
                        data-testid="button-create-callup"
                      >
                        <Plus className="h-4 w-4" />
                        Nueva Convocatoria
                      </Button>
                    </div>
                  )}

                  {(() => {
                    const visibleCallups = callups.filter(
                      (c: CallupSummary) => c.lifecycleStatus !== "archived",
                    );
                    const upcomingCallups = visibleCallups.filter(
                      (c: CallupSummary) => c.lifecycleStatus !== "past",
                    );
                    const pastCallups = visibleCallups.filter(
                      (c: CallupSummary) => c.lifecycleStatus === "past",
                    );
                    const shownCallups = callupSubTab === "upcoming" ? upcomingCallups : pastCallups;

                    return (
                      <>
                        <div className="flex gap-1 rounded-md bg-muted p-1 w-fit">
                          <Button
                            type="button"
                            size="sm"
                            variant={callupSubTab === "upcoming" ? "default" : "ghost"}
                            className="h-7 px-3"
                            onClick={() => setCallupSubTab("upcoming")}
                            data-testid="button-callups-tab-upcoming"
                          >
                            Próximas ({upcomingCallups.length})
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={callupSubTab === "past" ? "default" : "ghost"}
                            className="h-7 px-3"
                            onClick={() => setCallupSubTab("past")}
                            data-testid="button-callups-tab-past"
                          >
                            Pasadas ({pastCallups.length})
                          </Button>
                        </div>

                        {callupsLoading ? (
                          <Card>
                            <CardContent className="p-8 flex items-center justify-center">
                              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            </CardContent>
                          </Card>
                        ) : shownCallups.length === 0 ? (
                          <Card>
                            <CardContent className="p-8 text-center">
                              <Megaphone className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                              <p className="text-muted-foreground">
                                {callupSubTab === "upcoming" ? "No hay convocatorias próximas" : "No hay convocatorias pasadas"}
                              </p>
                              {canManageMatches && callupSubTab === "upcoming" && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Crea una convocatoria para organizar partidos
                                </p>
                              )}
                            </CardContent>
                          </Card>
                        ) : (
                          <div className="space-y-3">
                            {shownCallups.map((callup: CallupSummary) => (
                              <Card
                                key={callup.id}
                                className="cursor-pointer hover-elevate"
                                onClick={() => setLocation(`/leagues/${leagueId}/callups/${callup.id}`)}
                                data-testid={`card-callup-${callup.id}`}
                              >
                                <CardContent className="p-4">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <h4
                                        className="font-medium truncate"
                                        data-testid={`text-callup-title-${callup.id}`}
                                      >
                                        {callup.title}
                                      </h4>
                                      <p
                                        className="text-sm text-muted-foreground mt-1"
                                        data-testid={`text-callup-date-${callup.id}`}
                                      >
                                        <Calendar className="h-3 w-3 inline mr-1" />
                                        {formatCallupDateLabel(callup.date, callup.timezone)}{" "}
                                        {formatCallupTimeLabel(callup.date, callup.timezone)}
                                      </p>
                                      {callup.location && (
                                        <p className="text-sm text-muted-foreground mt-0.5">
                                          <MapPin className="h-3 w-3 inline mr-1" />
                                          {callup.location}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1.5">
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
                                          data-testid={`button-copy-callup-link-${callup.id}`}
                                          aria-label="Copiar enlace de convocatoria"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            const url = leagueCallupUrl(String(leagueId), callup.id);
                                            try {
                                              if (hasNativeCopy()) {
                                                const r = await nativeCopy(url);
                                                if (r?.ok) {
                                                  toast({ title: "Enlace copiado al portapapeles" });
                                                  return;
                                                }
                                              }
                                              await navigator.clipboard.writeText(url);
                                              toast({ title: "Enlace copiado al portapapeles" });
                                            } catch {
                                              toast({ title: "No se pudo copiar el enlace", variant: "destructive" });
                                            }
                                          }}
                                        >
                                          <Copy className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
                                          data-testid={`button-share-callup-${callup.id}`}
                                          aria-label="Compartir convocatoria"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            const url = leagueCallupUrl(String(leagueId), callup.id);
                                            const shareData = {
                                              title: `Convocatoria: ${callup.title}`,
                                              text: `¡Únete a esta convocatoria en CourtVibe!`,
                                              url,
                                            };
                                            const copyWithFallback = async () => {
                                              try {
                                                if (hasNativeCopy()) {
                                                  const r = await nativeCopy(url);
                                                  if (r?.ok) {
                                                    toast({ title: "Enlace copiado al portapapeles" });
                                                    return;
                                                  }
                                                }
                                                await navigator.clipboard.writeText(url);
                                                toast({ title: "Enlace copiado al portapapeles" });
                                              } catch {
                                                toast({ title: "No se pudo copiar el enlace", variant: "destructive" });
                                              }
                                            };
                                            try {
                                              if (hasNativeShare()) {
                                                const result = await nativeShare(shareData);
                                                if (result?.ok || result?.cancelled) return;
                                              }
                                              if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                                                await navigator.share(shareData);
                                              } else {
                                                await copyWithFallback();
                                              }
                                            } catch (error) {
                                              if ((error as Error).name !== "AbortError") {
                                                await copyWithFallback();
                                              }
                                            }
                                          }}
                                        >
                                          <Share2 className="h-3.5 w-3.5" />
                                        </Button>
                                        <Badge
                                          variant={
                                            callup.status === "open" ? "default" : callup.status === "closed" ? "secondary" : "outline"
                                          }
                                          className={cn(
                                            callup.status === "open" && "bg-green-600 text-white",
                                            callup.status === "closed" && "bg-orange-500 text-white"
                                          )}
                                          data-testid={`badge-callup-status-${callup.id}`}
                                        >
                                          {callup.status === "open" ? "Abierta" : callup.status === "closed" ? "Cerrada" : "Completada"}
                                        </Badge>
                                      </div>
                                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Users className="h-3 w-3" />
                                        {callup.signupCount ?? 0}/{callup.maxPlayers} jugadores
                                      </span>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}

              {/* ---- Americanos ---- */}
              {eventosSubTab === "americanos" && (
                <LeagueAmericanosSection
                  leagueId={String(leagueId)}
                  members={(data?.members ?? []).map((m) => ({
                    userId: m.userId,
                    userName: m.userName,
                    userImage: m.userImage,
                    role: m.role,
                  }))}
                  isAdmin={canManageMatches}
                />
              )}

              {/* ---- Torneos ---- */}
              {eventosSubTab === "torneos" && (
                <LeagueTournamentsSection
                  leagueId={String(leagueId)}
                  members={(data?.members ?? []).map((m) => ({
                    userId: m.userId,
                    userName: m.userName,
                    userImage: m.userImage,
                    role: m.role,
                  }))}
                  isAdmin={canManageMatches}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>


      {/* New Season Confirmation Dialog */}
      <AlertDialog open={showNewSeasonConfirm} onOpenChange={setShowNewSeasonConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Iniciar Nueva Temporada
            </AlertDialogTitle>
            <AlertDialogDescription>
              Al iniciar una nueva temporada:
              <ul className="list-disc ml-4 mt-2 space-y-1 text-left">
                <li>Se guardarán las clasificaciones actuales</li>
                <li>Los ratings de todos los miembros se reiniciarán</li>
                <li>Los partidos futuros contarán para la nueva temporada</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 text-left">
            <div className="flex items-center gap-1.5">
              <Label className="text-sm font-medium">Modo de inicio del ranking</Label>
              <Popover open={showRatingModeHelp} onOpenChange={setShowRatingModeHelp}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-5 w-5"
                    data-testid="button-rating-mode-help-new-season"
                  >
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="max-w-xs text-sm space-y-1.5">
                  <p>
                    <strong>Por nivel:</strong> los jugadores con mejor rating global empiezan con ventaja en la tabla.
                  </p>
                  <p>
                    <strong>Parejo:</strong> todos arrancan en 1000 puntos y el ranking se define solo por los partidos jugados en esta temporada.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <RadioGroup
              value={newSeasonRatingMode}
              onValueChange={(v) => setNewSeasonRatingMode(v as "skill_based" | "equal")}
              className="gap-2"
            >
              <label className="flex items-center gap-2 rounded-md border p-2.5 cursor-pointer hover:bg-muted/50">
                <RadioGroupItem value="skill_based" data-testid="radio-rating-mode-skill-new-season" />
                <span className="text-sm">Por nivel (recomendado)</span>
              </label>
              <label className="flex items-center gap-2 rounded-md border p-2.5 cursor-pointer hover:bg-muted/50">
                <RadioGroupItem value="equal" data-testid="radio-rating-mode-equal-new-season" />
                <span className="text-sm">Parejo (todos empiezan en 1000)</span>
              </label>
            </RadioGroup>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => startNewSeasonMutation.mutate()}
              disabled={startNewSeasonMutation.isPending}
              data-testid="button-confirm-new-season"
            >
              {startNewSeasonMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Iniciar Temporada
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* End Season Confirmation Dialog */}
      <AlertDialog open={showEndSeasonConfirm} onOpenChange={setShowEndSeasonConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Terminar Temporada
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres terminar la temporada actual? Las clasificaciones finales serán guardadas y no se podrán modificar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const activeSeason = seasons?.find(s => s.status === "active");
                if (activeSeason) {
                  endSeasonMutation.mutate(activeSeason.id);
                }
              }}
              disabled={endSeasonMutation.isPending}
              data-testid="button-confirm-end-season"
            >
              {endSeasonMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Terminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Past Season Rankings Dialog */}
      <Dialog open={!!selectedPastSeason} onOpenChange={(open) => !open && setSelectedPastSeason(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              {selectedPastSeason?.name}
            </DialogTitle>
          </DialogHeader>
          {pastSeasonLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : pastSeasonData?.members && pastSeasonData.members.length > 0 ? (
            <div className="divide-y">
              {pastSeasonData.members.map((member, index) => (
                <div key={member.id} className="flex items-center gap-3 py-3">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
                    index === 0 && "bg-yellow-500 text-white",
                    index === 1 && "bg-gray-400 text-white",
                    index === 2 && "bg-amber-700 text-white",
                    index > 2 && "bg-muted text-muted-foreground"
                  )}>
                    {member.finalRank}
                  </div>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={member.user.profileImageUrl || undefined} />
                    <AvatarFallback className="text-xs">
                      {(member.user.firstName?.[0] || '') + (member.user.lastName?.[0] || '')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {member.user.firstName} {member.user.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {member.matchesPlayed} partidos · {member.matchesWon} ganados
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{member.finalRating}</p>
                    <p className="text-xs text-muted-foreground">ELO</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No hay datos de clasificación para esta temporada
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Seasons Onboarding Dialog */}
      <Dialog open={showSeasonsOnboarding} onOpenChange={(open) => !open && dismissSeasonsOnboarding()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              ¿Cómo funcionan las temporadas?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Trophy className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">Ratings se reinician</p>
                <p className="text-xs text-muted-foreground">
                  Al iniciar una nueva temporada, los ratings ELO de todos los miembros vuelven a su valor inicial basado en su nivel.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Medal className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">Historial guardado</p>
                <p className="text-xs text-muted-foreground">
                  Las clasificaciones finales de cada temporada se guardan para siempre. Puedes ver quién ganó cada temporada.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Info className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">Tu nivel global no cambia</p>
                <p className="text-xs text-muted-foreground">
                  Solo se reinicia el rating dentro de esta liga. Tu nivel global (1.0-7.0) permanece igual.
                </p>
              </div>
            </div>

            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Nota:</span> Solo los administradores pueden iniciar o terminar temporadas.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={dismissSeasonsOnboarding} data-testid="button-dismiss-onboarding">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Editar Liga
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="league-name">Nombre de la liga</Label>
              <Input
                id="league-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Nombre de la liga"
                data-testid="input-league-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="league-description">Descripción</Label>
              <Textarea
                id="league-description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Descripción opcional de la liga"
                rows={2}
                data-testid="input-league-description"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Imagen de la liga
              </Label>
              <div className="flex items-center gap-2">
                <label
                  htmlFor="league-image-upload"
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-3 cursor-pointer transition-colors",
                    "hover:border-primary hover:bg-primary/5",
                    uploadingImage && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {uploadingImage ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">Subiendo...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {editForm.imageUrl ? "Cambiar" : "Subir imagen"}
                      </span>
                    </>
                  )}
                  <input
                    id="league-image-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    data-testid="input-league-image-upload"
                  />
                </label>
                {editForm.imageUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setEditForm({ ...editForm, imageUrl: "" })}
                    data-testid="button-remove-league-image"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {editForm.imageUrl && (
                <div className="mt-2 rounded-lg overflow-hidden border">
                  <img 
                    src={resolveAssetUrl(editForm.imageUrl)} 
                    alt="Preview" 
                    className="w-full h-24 object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="space-y-0.5">
                <Label htmlFor="league-private" className="text-sm">Liga privada</Label>
                <p className="text-xs text-muted-foreground">
                  Solo con enlace de invitación
                </p>
              </div>
              <Switch
                id="league-private"
                checked={editForm.isPrivate}
                onCheckedChange={(checked) => setEditForm({ ...editForm, isPrivate: checked })}
                data-testid="switch-league-private"
              />
            </div>
          </div>
          
          {/* Action Buttons - Mobile optimized */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1"
                data-testid="button-cancel-edit"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={updateLeagueMutation.isPending || !editForm.name.trim()}
                className="flex-1"
                data-testid="button-save-league"
              >
                {updateLeagueMutation.isPending ? "Guardando..." : "Guardar"}
              </Button>
            </div>
            {isOwner && (
              <Button
                variant="ghost"
                onClick={() => {
                  setShowEditModal(false);
                  setTimeout(() => setShowDeleteConfirm(true), 100);
                }}
                className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                data-testid="button-delete-league"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar Liga
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Eliminar Liga
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar la liga <strong>{league?.name}</strong>? 
              Esta acción eliminará todos los partidos, miembros y datos asociados. 
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteLeagueMutation.mutate()}
              disabled={deleteLeagueMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteLeagueMutation.isPending ? "Eliminando..." : "Eliminar Liga"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Match Modal */}
      <Dialog open={showEditMatchModal} onOpenChange={setShowEditMatchModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Editar Resultado
            </DialogTitle>
          </DialogHeader>
          {selectedMatch && (
            <div className="space-y-4 py-4">
              <div className="text-sm text-muted-foreground text-center">
                {[selectedMatch.team1Player1Name, selectedMatch.team1Player2Name].filter(Boolean).join(" / ")}
                {" vs "}
                {[selectedMatch.team2Player1Name, selectedMatch.team2Player2Name].filter(Boolean).join(" / ")}
              </div>
              <div className="grid grid-cols-3 gap-4 items-center">
                <div className="space-y-2">
                  <Label className="text-center block">Equipo 1</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editMatchForm.team1Score}
                    onChange={(e) => setEditMatchForm({ ...editMatchForm, team1Score: parseInt(e.target.value) || 0 })}
                    className="text-center text-lg"
                    data-testid="input-team1-score"
                  />
                </div>
                <div className="text-center text-2xl font-bold text-muted-foreground">-</div>
                <div className="space-y-2">
                  <Label className="text-center block">Equipo 2</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editMatchForm.team2Score}
                    onChange={(e) => setEditMatchForm({ ...editMatchForm, team2Score: parseInt(e.target.value) || 0 })}
                    className="text-center text-lg"
                    data-testid="input-team2-score"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Ganador</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={editMatchForm.winner === "team1" ? "default" : "outline"}
                    onClick={() => setEditMatchForm({ ...editMatchForm, winner: "team1" })}
                    className="w-full"
                    data-testid="button-winner-team1"
                  >
                    <Trophy className={cn("h-4 w-4 mr-2", editMatchForm.winner === "team1" && "text-yellow-400")} />
                    Equipo 1
                  </Button>
                  <Button
                    type="button"
                    variant={editMatchForm.winner === "team2" ? "default" : "outline"}
                    onClick={() => setEditMatchForm({ ...editMatchForm, winner: "team2" })}
                    className="w-full"
                    data-testid="button-winner-team2"
                  >
                    <Trophy className={cn("h-4 w-4 mr-2", editMatchForm.winner === "team2" && "text-yellow-400")} />
                    Equipo 2
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Al guardar, los rankings de todos los jugadores serán recalculados.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditMatchModal(false)}
              data-testid="button-cancel-edit-match"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveMatchEdit}
              disabled={updateMatchMutation.isPending}
              data-testid="button-save-match"
            >
              {updateMatchMutation.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Match Confirmation */}
      <AlertDialog open={showDeleteMatchConfirm} onOpenChange={setShowDeleteMatchConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Eliminar Partido
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar este partido? 
              Los rankings de todos los jugadores serán recalculados. 
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-match">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMatch}
              disabled={deleteMatchMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-match"
            >
              {deleteMatchMutation.isPending ? "Eliminando..." : "Eliminar Partido"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Leave League Confirmation */}
      <AlertDialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5" />
              Abandonar Liga
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas abandonar esta liga? 
              Tu historial de partidos se mantendrá, pero dejarás de ser miembro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-leave-league">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => leaveLeagueMutation.mutate()}
              disabled={leaveLeagueMutation.isPending}
              data-testid="button-confirm-leave-league"
            >
              {leaveLeagueMutation.isPending ? "Abandonando..." : "Abandonar Liga"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Member Confirmation */}
      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Eliminar Miembro
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar a {memberToRemove?.name} de la liga? 
              Su historial de partidos se mantendrá.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-remove-member">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (memberToRemove) {
                  removeMemberMutation.mutate(memberToRemove.id);
                  setMemberToRemove(null);
                }
              }}
              disabled={removeMemberMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-remove-member"
            >
              {removeMemberMutation.isPending ? "Eliminando..." : "Eliminar Miembro"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PlayerPreviewModal 
        playerId={selectedPlayerId} 
        open={!!selectedPlayerId} 
        onOpenChange={(open) => !open && setSelectedPlayerId(null)} 
      />
      
      {/* Match Summary Drawer */}
      <MatchHistoryDrawer
        open={showMatchSummaryDrawer}
        onOpenChange={setShowMatchSummaryDrawer}
        match={matchForSummary ? (() => {
          const userId = data?.userMembership?.userId;
          const isOnTeam1 = userId === matchForSummary.team1Player1Id || userId === matchForSummary.team1Player2Id;
          const isOnTeam2 = userId === matchForSummary.team2Player1Id || userId === matchForSummary.team2Player2Id;
          const isParticipant = isOnTeam1 || isOnTeam2;
          
          // For participants, use their actual team. For spectators, show from team 1's perspective
          const userTeam = isOnTeam1 ? 1 : (isOnTeam2 ? 2 : 1);
          const userWon = matchForSummary.winner === userTeam;
          
          // Get rating changes based on user's team (or team 1 for spectators)
          const ratingChanges = matchForSummary.ratingChanges;
          let ratingBefore = 0;
          let ratingAfter = 0;
          let ratingDelta = 0;
          
          if (ratingChanges) {
            const p1 = isOnTeam2 ? ratingChanges.team2Player1 : ratingChanges.team1Player1;
            ratingBefore = p1?.before || 0;
            ratingAfter = p1?.after || 0;
            ratingDelta = p1?.delta || 0;
          }
          
          // Calculate expected probability from rating changes
          const team1Rating = ratingChanges 
            ? (ratingChanges.team1Player1?.before || 0) + (ratingChanges.team1Player2?.before || 0)
            : 0;
          const team2Rating = ratingChanges
            ? (ratingChanges.team2Player1?.before || 0) + (ratingChanges.team2Player2?.before || 0)
            : 0;
          const avgTeam1 = ratingChanges?.team1Player2 ? team1Rating / 2 : ratingChanges?.team1Player1?.before || 0;
          const avgTeam2 = ratingChanges?.team2Player2 ? team2Rating / 2 : ratingChanges?.team2Player1?.before || 0;
          const expectedProb = Math.round((1 / (1 + Math.pow(10, (avgTeam2 - avgTeam1) / 400))) * 100);
          
          // Determine opponents and teammate names based on perspective
          const opponents: string[] = [];
          let teammate: string | undefined;
          
          if (isOnTeam1) {
            // User is on team 1, opponents are team 2
            if (matchForSummary.team2Player1Name) opponents.push(matchForSummary.team2Player1Name);
            if (matchForSummary.team2Player2Name) opponents.push(matchForSummary.team2Player2Name);
            // Teammate is the other player on team 1
            if (userId === matchForSummary.team1Player1Id) {
              teammate = matchForSummary.team1Player2Name || undefined;
            } else {
              teammate = matchForSummary.team1Player1Name || undefined;
            }
          } else if (isOnTeam2) {
            // User is on team 2, opponents are team 1
            if (matchForSummary.team1Player1Name) opponents.push(matchForSummary.team1Player1Name);
            if (matchForSummary.team1Player2Name) opponents.push(matchForSummary.team1Player2Name);
            // Teammate is the other player on team 2
            if (userId === matchForSummary.team2Player1Id) {
              teammate = matchForSummary.team2Player2Name || undefined;
            } else {
              teammate = matchForSummary.team2Player1Name || undefined;
            }
          } else {
            // Spectator: show from team 1's perspective, team 2 as opponents
            if (matchForSummary.team2Player1Name) opponents.push(matchForSummary.team2Player1Name);
            if (matchForSummary.team2Player2Name) opponents.push(matchForSummary.team2Player2Name);
            teammate = matchForSummary.team1Player2Name || undefined;
          }

          return {
            id: matchForSummary.id,
            type: 'league' as const,
            leagueId: matchForSummary.leagueId,
            leagueName: data?.league?.name,
            playedAt: matchForSummary.playedAt || new Date(),
            status: matchForSummary.status || 'approved',
            winner: matchForSummary.winner,
            score: matchForSummary.score,
            userTeam,
            userWon,
            ratingBefore,
            ratingAfter,
            ratingDelta,
            opponents,
            teammate,
            expectedProbability: expectedProb,
            reportedById: matchForSummary.reportedById,
            team1Player1Id: matchForSummary.team1Player1Id,
            team1Player2Id: matchForSummary.team1Player2Id,
            team2Player1Id: matchForSummary.team2Player1Id,
            team2Player2Id: matchForSummary.team2Player2Id,
            team1Player1Name: matchForSummary.team1Player1Name,
            team1Player2Name: matchForSummary.team1Player2Name,
            team2Player1Name: matchForSummary.team2Player1Name,
            team2Player2Name: matchForSummary.team2Player2Name,
          };
        })() : null}
        currentUserId={data?.userMembership?.userId}
        isLeagueAdmin={data?.userMembership?.role === "owner" || data?.userMembership?.role === "admin"}
        onEdit={(match) => {
          setMatchToEdit(match);
          setShowEditMatchDialog(true);
          setShowMatchSummaryDrawer(false);
        }}
        viewerName={(() => {
          const userId = data?.userMembership?.userId;
          const isParticipant = matchForSummary && (
            userId === matchForSummary.team1Player1Id ||
            userId === matchForSummary.team1Player2Id ||
            userId === matchForSummary.team2Player1Id ||
            userId === matchForSummary.team2Player2Id
          );
          if (isParticipant) {
            return data?.members?.find(m => m.userId === userId)?.userName || 'Tú';
          }
          // For spectators, show team 1 player 1 name
          return matchForSummary?.team1Player1Name || 'Equipo 1';
        })()}
        isViewerParticipant={!!data?.userMembership && matchForSummary ? (
          data.userMembership.userId === matchForSummary.team1Player1Id ||
          data.userMembership.userId === matchForSummary.team1Player2Id ||
          data.userMembership.userId === matchForSummary.team2Player1Id ||
          data.userMembership.userId === matchForSummary.team2Player2Id
        ) : false}
      />
      
      <EditPendingMatchDialog
        open={showEditMatchDialog}
        onOpenChange={setShowEditMatchDialog}
        match={matchToEdit}
        onSuccess={() => {
          setMatchToEdit(null);
          queryClient.invalidateQueries({ queryKey: ["/api/leagues", leagueId.toString()] });
        }}
      />

      <EditScheduledMatchDialog
        match={scheduledEditTarget}
        members={(data?.members ?? []).map((m) => ({
          userId: m.userId,
          userName: m.userName,
        }))}
        requireAllPlayers={data?.league.matchFormat === "doubles"}
        onClose={() => setScheduledEditTarget(null)}
        onSuccess={() =>
          queryClient.invalidateQueries({
            queryKey: ["/api/leagues", leagueId.toString()],
          })
        }
      />

      <RegisterScheduledMatchResultDialog
        match={scheduledResultTarget}
        onClose={() => setScheduledResultTarget(null)}
        onSuccess={() =>
          queryClient.invalidateQueries({
            queryKey: ["/api/leagues", leagueId.toString()],
          })
        }
      />

      <LockedScheduledResultDialog
        open={!!scheduledLockedTarget}
        scheduledDate={scheduledLockedTarget?.scheduledDate ?? null}
        scheduledTime={scheduledLockedTarget?.scheduledTime ?? null}
        onClose={() => setScheduledLockedTarget(null)}
      />

      <CompletedScheduledMatchDetailsDialog
        matchId={scheduledDetailTargetId}
        onClose={() => setScheduledDetailTargetId(null)}
        canDelete={(() => {
          if (!scheduledDetailTargetId) return false;
          const sm = data?.scheduledMatches?.find(
            (m) => m.id === scheduledDetailTargetId,
          );
          if (!sm) return false;
          if (sm.status !== "scheduled" && sm.status !== "score_pending") {
            return false;
          }
          const isParticipant =
            !!currentUserId &&
            (sm.team1Player1Id === currentUserId ||
              sm.team1Player2Id === currentUserId ||
              sm.team2Player1Id === currentUserId ||
              sm.team2Player2Id === currentUserId);
          const isLeagueAdmin =
            data?.userMembership?.role === "owner" ||
            data?.userMembership?.role === "admin";
          return isParticipant || isLeagueAdmin;
        })()}
        onRequestDelete={(id) => {
          const sm = data?.scheduledMatches?.find((m) => m.id === id);
          if (!sm) return;
          setScheduledDetailTargetId(null);
          setScheduledDeleteTarget(sm);
        }}
      />

      <AlertDialog
        open={!!scheduledDeleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleteScheduledMatchMutation.isPending) {
            setScheduledDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent data-testid="dialog-confirm-delete-scheduled">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar partido agendado</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Los demás jugadores
              recibirán una notificación de la cancelación.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleteScheduledMatchMutation.isPending}
              data-testid="button-cancel-delete-scheduled"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteScheduledMatchMutation.isPending}
              onClick={() => {
                if (scheduledDeleteTarget) {
                  deleteScheduledMatchMutation.mutate(
                    scheduledDeleteTarget.id,
                  );
                }
              }}
              data-testid="button-confirm-delete-scheduled"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteScheduledMatchMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
